import React, { useState, useEffect, lazy, Suspense } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { I18nProvider, useI18n, ROUTE_MAPPINGS } from './context/I18nContext';
import { CookieProvider } from './context/CookieContext';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import AccessibilityWidget from './components/AccessibilityWidget';
import ErrorBoundary from './components/ErrorBoundary';
import NotFoundPage from './components/NotFoundPage';
import ReverseForm from './components/ReverseForm';
import { getArticleUrl } from './data/blog/articles';

// Lazy loaded: pulls in jsPDF + jsbarcode, ~100KB+ gzip that only /tessera-sanitaria
// needs, but was shipping in every page's main bundle since it was a static import.
const HealthCardGenerator = lazy(() => import('./components/HealthCardGenerator'));

// Lazy loaded: each of these is only rendered on its own route, but was a static
// import shipping in EVERY page's main.js (300KB+ gzip on a text-only page like
// /inverso, delaying LCP on mobile under CPU throttling even though the prerendered
// HTML paints as soon as main.css loads). CalculatorForm was the last holdout
// (kept static because it's HomePage's own form) but it still pulls in
// @radix-ui/react-radio-group, used nowhere else in the app, so non-home routes
// like /inverso were paying for it too. HomePage's LCP element is the hero text
// above this section, so deferring it doesn't touch home's own LCP.
const CalculatorForm = lazy(() => import('./components/CalculatorForm'));
const VerifyForm = lazy(() => import('./components/VerifyForm'));
const MunicipalitySearch = lazy(() => import('./components/MunicipalitySearch'));
const HowItWorks = lazy(() => import('./components/HowItWorks'));
const FAQPage = lazy(() => import('./components/FAQPage'));
const GuidaCodiceFiscale = lazy(() => import('./components/GuidaCodiceFiscale'));
const OmocodiaPage = lazy(() => import('./components/OmocodiaPage'));
const ProvincePage = lazy(() => import('./components/ProvincePage'));
const RegionPage = lazy(() => import('./components/RegionPage'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const ContactPage = lazy(() => import('./components/ContactPage'));

// Blog imports - Lazy loaded for code splitting
const BlogIndex = lazy(() => import('./components/blog/BlogIndex'));
const BlogArticles = {
  ArticleComeCalcolareCodiceFiscale: lazy(() => import('./components/blog/articles/ArticleComeCalcolareCodiceFiscale')),
  ArticleCodiceFiscaleStranieri: lazy(() => import('./components/blog/articles/ArticleCodiceFiscaleStranieri')),
  ArticleCodiceFiscaleNeonati: lazy(() => import('./components/blog/articles/ArticleCodiceFiscaleNeonati')),
  ArticleDocumentiCodiceFiscale: lazy(() => import('./components/blog/articles/ArticleDocumentiCodiceFiscale')),
  ArticleOmocodiaSpiegata: lazy(() => import('./components/blog/articles/ArticleOmocodiaSpiegata')),
  ArticleTesseraSanitariaGuida: lazy(() => import('./components/blog/articles/ArticleTesseraSanitariaGuida')),
  ArticleErroriCalcolo: lazy(() => import('./components/blog/articles/ArticleErroriCalcolo')),
  ArticleCarattereControllo: lazy(() => import('./components/blog/articles/ArticleCarattereControllo')),
  ArticleCodiciCatastali: lazy(() => import('./components/blog/articles/ArticleCodiciCatastali')),
  ArticleDifferenzaCfPiva: lazy(() => import('./components/blog/articles/ArticleDifferenzaCfPiva')),
  ArticleStrutturaCF: lazy(() => import('./components/blog/articles/ArticleStrutturaCF')),
  ArticleTesseraEuropeaTEAM: lazy(() => import('./components/blog/articles/ArticleTesseraEuropeaTEAM')),
  ArticleCFCartaIdentita: lazy(() => import('./components/blog/articles/ArticleCFCartaIdentita')),
  ArticleCFSpid: lazy(() => import('./components/blog/articles/ArticleCFSpid')),
  ArticleCFCittadiniUE: lazy(() => import('./components/blog/articles/ArticleCFCittadiniUE')),
  ArticleCFProvvisorio: lazy(() => import('./components/blog/articles/ArticleCFProvvisorio')),
  ArticleCambioDati: lazy(() => import('./components/blog/articles/ArticleCambioDati')),
  ArticleCFDeceduti: lazy(() => import('./components/blog/articles/ArticleCFDeceduti')),
  ArticleVerificaOnline: lazy(() => import('./components/blog/articles/ArticleVerificaOnline')),
  ArticleStoriaCF: lazy(() => import('./components/blog/articles/ArticleStoriaCF'))
};

// Loading skeleton for lazy components
const LazyLoadingFallback = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="animate-pulse flex flex-col items-center gap-4">
      <div className="w-12 h-12 bg-emerald-200 rounded-full"></div>
      <div className="h-4 w-32 bg-gray-200 rounded"></div>
    </div>
  </div>
);

import SEOHead from './components/SEOHead';
import { trackPageView } from './utils/analytics';
import { Zap, Lock, Globe, CheckCircle } from 'lucide-react';

// Multilingual content for HomePage - all 8 languages
const HOMEPAGE_FEATURES = {
  it: [
    { title: 'Istantaneo', desc: 'Calcolo in meno di 2 secondi' },
    { title: '100% Privacy', desc: 'Nessun dato salvato' },
    { title: '8 Lingue', desc: 'Supporto multilingua' },
    { title: 'Preciso', desc: 'Algoritmo ufficiale' },
  ],
  en: [
    { title: 'Instant', desc: 'Calculate in under 2 seconds' },
    { title: '100% Privacy', desc: 'No data saved' },
    { title: '8 Languages', desc: 'Multilingual support' },
    { title: 'Accurate', desc: 'Official algorithm' },
  ],
  de: [
    { title: 'Sofort', desc: 'Berechnung in unter 2 Sekunden' },
    { title: '100% Datenschutz', desc: 'Keine Daten gespeichert' },
    { title: '8 Sprachen', desc: 'Mehrsprachige Unterstützung' },
    { title: 'Präzise', desc: 'Offizieller Algorithmus' },
  ],
  fr: [
    { title: 'Instantané', desc: 'Calcul en moins de 2 secondes' },
    { title: '100% Confidentialité', desc: 'Aucune donnée sauvegardée' },
    { title: '8 Langues', desc: 'Support multilingue' },
    { title: 'Précis', desc: 'Algorithme officiel' },
  ],
  es: [
    { title: 'Instantáneo', desc: 'Cálculo en menos de 2 segundos' },
    { title: '100% Privacidad', desc: 'Ningún dato guardado' },
    { title: '8 Idiomas', desc: 'Soporte multilingüe' },
    { title: 'Preciso', desc: 'Algoritmo oficial' },
  ],
  pt: [
    { title: 'Instantâneo', desc: 'Cálculo em menos de 2 segundos' },
    { title: '100% Privacidade', desc: 'Nenhum dado salvo' },
    { title: '8 Idiomas', desc: 'Suporte multilíngue' },
    { title: 'Preciso', desc: 'Algoritmo oficial' },
  ],
  ro: [
    { title: 'Instantaneu', desc: 'Calcul în mai puțin de 2 secunde' },
    { title: '100% Confidențialitate', desc: 'Niciun date salvate' },
    { title: '8 Limbi', desc: 'Suport multilingv' },
    { title: 'Precis', desc: 'Algoritm oficial' },
  ],
  pl: [
    { title: 'Natychmiastowo', desc: 'Obliczenie w mniej niż 2 sekundy' },
    { title: '100% Prywatność', desc: 'Żadne dane nie są zapisywane' },
    { title: '8 Języków', desc: 'Wsparcie wielojęzyczne' },
    { title: 'Dokładny', desc: 'Oficjalny algorytm' },
  ]
};

const HOMEPAGE_FAQS = {
  it: [
    { q: 'Come si calcola il Codice Fiscale?', a: 'Il Codice Fiscale si calcola partendo da cognome (3 lettere), nome (3 lettere), anno di nascita (2 cifre), mese (1 lettera), giorno (2 cifre, +40 per donne), codice comune (4 caratteri) e carattere di controllo.' },
    { q: 'Quanto costa calcolare il Codice Fiscale online?', a: 'Su CodiceFiscale.expert il calcolo è completamente gratuito, senza limiti e senza registrazione.' },
    { q: 'Il Codice Fiscale generato è ufficiale?', a: "Il nostro calcolatore genera un Codice Fiscale corretto algoritmicamente secondo le regole dell'Agenzia delle Entrate. Per il rilascio ufficiale è necessario rivolgersi all'Agenzia delle Entrate." },
    { q: "Cos'è l'omocodia nel Codice Fiscale?", a: "L'omocodia si verifica quando due persone hanno lo stesso Codice Fiscale calcolato. L'Agenzia delle Entrate risolve sostituendo alcune cifre con lettere." },
    { q: 'Come funziona il calcolo per le donne?', a: 'Per le donne, al giorno di nascita viene aggiunto 40. Quindi se una donna è nata il 15, nel Codice Fiscale apparirà 55.' },
    { q: 'I miei dati vengono salvati?', a: 'NO. CodiceFiscale.expert non salva nessun dato personale. Tutti i calcoli avvengono nel tuo browser.' },
    { q: 'Come ottenere il Codice Fiscale per stranieri?', a: "Gli stranieri possono calcolare il loro CF inserendo i dati anagrafici e selezionando il paese di nascita estero. Per il rilascio ufficiale devono recarsi all'Agenzia delle Entrate." },
    { q: 'Il sito è conforme al GDPR?', a: 'Sì, CodiceFiscale.expert è pienamente conforme al GDPR. Non raccogliamo dati personali, tutti i calcoli avvengono nel browser.' }
  ],
  en: [
    { q: 'How is the Italian Tax Code calculated?', a: 'The Tax Code is calculated from: surname (3 letters), name (3 letters), birth year (2 digits), month (1 letter), day (2 digits, +40 for women), municipality code (4 characters) and control character.' },
    { q: 'Is the online calculator free?', a: 'Yes! CodiceFiscale.expert is completely free, with no limits and no registration required.' },
    { q: 'Is the generated Tax Code official?', a: 'Our calculator generates a Tax Code that is algorithmically correct according to the Revenue Agency rules. For official issuance, contact the Italian Revenue Agency.' },
    { q: 'What is omocodia?', a: 'Omocodia occurs when two people have the same calculated Tax Code. The Revenue Agency resolves this by replacing some digits with letters.' },
    { q: 'How does the calculation work for women?', a: 'For women, 40 is added to the birth day. So if a woman was born on the 15th, the Tax Code will show 55.' },
    { q: 'Is my data saved?', a: 'NO. CodiceFiscale.expert does not save any personal data. All calculations happen in your browser.' },
    { q: 'How do foreigners get an Italian Tax Code?', a: 'Foreigners can calculate their Tax Code by entering their personal data and selecting their foreign country of birth. For official issuance, visit the Italian Revenue Agency.' },
    { q: 'Is the site GDPR compliant?', a: 'Yes, CodiceFiscale.expert is fully GDPR compliant. We do not collect personal data, all calculations happen in the browser.' }
  ],
  de: [
    { q: 'Wie wird die italienische Steuernummer berechnet?', a: 'Die Steuernummer wird berechnet aus: Nachname (3 Buchstaben), Vorname (3 Buchstaben), Geburtsjahr (2 Ziffern), Monat (1 Buchstabe), Tag (2 Ziffern, +40 für Frauen), Gemeindecode (4 Zeichen) und Kontrollzeichen.' },
    { q: 'Ist der Online-Rechner kostenlos?', a: 'Ja! CodiceFiscale.expert ist völlig kostenlos, ohne Limits und ohne Registrierung.' },
    { q: 'Ist die generierte Steuernummer offiziell?', a: 'Unser Rechner generiert eine algorithmisch korrekte Steuernummer gemäß den Regeln der Finanzbehörde. Für die offizielle Ausstellung wenden Sie sich an die italienische Finanzbehörde.' },
    { q: 'Was ist Omocodia?', a: 'Omocodia tritt auf, wenn zwei Personen die gleiche berechnete Steuernummer haben. Die Finanzbehörde löst dies durch Ersetzen einiger Ziffern durch Buchstaben.' },
    { q: 'Wie funktioniert die Berechnung für Frauen?', a: 'Für Frauen werden 40 zum Geburtstag addiert. Wenn eine Frau am 15. geboren wurde, zeigt die Steuernummer 55.' },
    { q: 'Werden meine Daten gespeichert?', a: 'NEIN. CodiceFiscale.expert speichert keine persönlichen Daten. Alle Berechnungen erfolgen in Ihrem Browser.' },
    { q: 'Wie erhalten Ausländer eine italienische Steuernummer?', a: 'Ausländer können ihre Steuernummer berechnen, indem sie ihre persönlichen Daten eingeben und ihr Geburtsland auswählen. Für die offizielle Ausstellung besuchen Sie die italienische Finanzbehörde.' },
    { q: 'Ist die Seite DSGVO-konform?', a: 'Ja, CodiceFiscale.expert ist vollständig DSGVO-konform. Wir sammeln keine persönlichen Daten, alle Berechnungen erfolgen im Browser.' }
  ],
  fr: [
    { q: 'Comment le Code Fiscal italien est-il calculé?', a: 'Le Code Fiscal est calculé à partir de: nom (3 lettres), prénom (3 lettres), année de naissance (2 chiffres), mois (1 lettre), jour (2 chiffres, +40 pour les femmes), code commune (4 caractères) et caractère de contrôle.' },
    { q: 'Le calculateur en ligne est-il gratuit?', a: 'Oui! CodiceFiscale.expert est entièrement gratuit, sans limites et sans inscription.' },
    { q: 'Le Code Fiscal généré est-il officiel?', a: "Notre calculateur génère un Code Fiscal algorithmiquement correct selon les règles de l'Agence des Recettes. Pour l'émission officielle, contactez l'Agence des Recettes italienne." },
    { q: "Qu'est-ce que l'omocodia?", a: "L'omocodia se produit quand deux personnes ont le même Code Fiscal calculé. L'Agence des Recettes résout cela en remplaçant certains chiffres par des lettres." },
    { q: 'Comment fonctionne le calcul pour les femmes?', a: 'Pour les femmes, 40 est ajouté au jour de naissance. Donc si une femme est née le 15, le Code Fiscal affichera 55.' },
    { q: 'Mes données sont-elles sauvegardées?', a: 'NON. CodiceFiscale.expert ne sauvegarde aucune donnée personnelle. Tous les calculs se font dans votre navigateur.' },
    { q: 'Comment les étrangers obtiennent-ils un Code Fiscal italien?', a: "Les étrangers peuvent calculer leur Code Fiscal en entrant leurs données personnelles et en sélectionnant leur pays de naissance. Pour l'émission officielle, visitez l'Agence des Recettes italienne." },
    { q: 'Le site est-il conforme au RGPD?', a: 'Oui, CodiceFiscale.expert est entièrement conforme au RGPD. Nous ne collectons pas de données personnelles, tous les calculs se font dans le navigateur.' }
  ],
  es: [
    { q: '¿Cómo se calcula el Código Fiscal italiano?', a: 'El Código Fiscal se calcula a partir de: apellido (3 letras), nombre (3 letras), año de nacimiento (2 dígitos), mes (1 letra), día (2 dígitos, +40 para mujeres), código municipio (4 caracteres) y carácter de control.' },
    { q: '¿Es gratuita la calculadora online?', a: '¡Sí! CodiceFiscale.expert es completamente gratis, sin límites y sin registro.' },
    { q: '¿Es oficial el Código Fiscal generado?', a: 'Nuestra calculadora genera un Código Fiscal algorítmicamente correcto según las reglas de la Agencia Tributaria. Para la emisión oficial, contacte con la Agencia Tributaria italiana.' },
    { q: '¿Qué es la omocodia?', a: 'La omocodia ocurre cuando dos personas tienen el mismo Código Fiscal calculado. La Agencia Tributaria lo resuelve reemplazando algunos dígitos con letras.' },
    { q: '¿Cómo funciona el cálculo para mujeres?', a: 'Para las mujeres, se añade 40 al día de nacimiento. Así que si una mujer nació el 15, el Código Fiscal mostrará 55.' },
    { q: '¿Se guardan mis datos?', a: 'NO. CodiceFiscale.expert no guarda ningún dato personal. Todos los cálculos se realizan en tu navegador.' },
    { q: '¿Cómo obtienen los extranjeros un Código Fiscal italiano?', a: 'Los extranjeros pueden calcular su Código Fiscal ingresando sus datos personales y seleccionando su país de nacimiento. Para la emisión oficial, visite la Agencia Tributaria italiana.' },
    { q: '¿El sitio cumple con el RGPD?', a: 'Sí, CodiceFiscale.expert cumple totalmente con el RGPD. No recopilamos datos personales, todos los cálculos se realizan en el navegador.' }
  ],
  pt: [
    { q: 'Como é calculado o Código Fiscal italiano?', a: 'O Código Fiscal é calculado a partir de: sobrenome (3 letras), nome (3 letras), ano de nascimento (2 dígitos), mês (1 letra), dia (2 dígitos, +40 para mulheres), código município (4 caracteres) e caractere de controle.' },
    { q: 'A calculadora online é gratuita?', a: 'Sim! CodiceFiscale.expert é completamente grátis, sem limites e sem registro.' },
    { q: 'O Código Fiscal gerado é oficial?', a: 'Nossa calculadora gera um Código Fiscal algoritmicamente correto segundo as regras da Agência das Receitas. Para emissão oficial, contate a Agência das Receitas italiana.' },
    { q: 'O que é omocodia?', a: 'Omocodia ocorre quando duas pessoas têm o mesmo Código Fiscal calculado. A Agência das Receitas resolve isso substituindo alguns dígitos por letras.' },
    { q: 'Como funciona o cálculo para mulheres?', a: 'Para mulheres, 40 é adicionado ao dia de nascimento. Então se uma mulher nasceu no dia 15, o Código Fiscal mostrará 55.' },
    { q: 'Meus dados são salvos?', a: 'NÃO. CodiceFiscale.expert não salva nenhum dado pessoal. Todos os cálculos acontecem no seu navegador.' },
    { q: 'Como estrangeiros obtêm um Código Fiscal italiano?', a: 'Estrangeiros podem calcular seu Código Fiscal inserindo seus dados pessoais e selecionando seu país de nascimento. Para emissão oficial, visite a Agência das Receitas italiana.' },
    { q: 'O site é compatível com LGPD?', a: 'Sim, CodiceFiscale.expert é totalmente compatível com LGPD/GDPR. Não coletamos dados pessoais, todos os cálculos acontecem no navegador.' }
  ],
  ro: [
    { q: 'Cum se calculează Codul Fiscal italian?', a: 'Codul Fiscal se calculează din: nume (3 litere), prenume (3 litere), anul nașterii (2 cifre), luna (1 literă), ziua (2 cifre, +40 pentru femei), codul comunei (4 caractere) și caracterul de control.' },
    { q: 'Calculatorul online este gratuit?', a: 'Da! CodiceFiscale.expert este complet gratuit, fără limite și fără înregistrare.' },
    { q: 'Codul Fiscal generat este oficial?', a: 'Calculatorul nostru generează un Cod Fiscal corect algoritmic conform regulilor Agenției Veniturilor. Pentru emiterea oficială, contactați Agenția Veniturilor italiana.' },
    { q: 'Ce este omocodia?', a: 'Omocodia apare când două persoane au același Cod Fiscal calculat. Agenția Veniturilor rezolvă acest lucru înlocuind unele cifre cu litere.' },
    { q: 'Cum funcționează calculul pentru femei?', a: 'Pentru femei, se adaugă 40 la ziua nașterii. Deci dacă o femeie s-a născut pe 15, Codul Fiscal va arăta 55.' },
    { q: 'Datele mele sunt salvate?', a: 'NU. CodiceFiscale.expert nu salvează niciun date personal. Toate calculele se fac în browser-ul tău.' },
    { q: 'Cum obțin străinii un Cod Fiscal italian?', a: 'Străinii pot calcula Codul lor Fiscal introducând datele personale și selectând țara de naștere. Pentru emiterea oficială, vizitați Agenția Veniturilor italiana.' },
    { q: 'Site-ul este conform GDPR?', a: 'Da, CodiceFiscale.expert este complet conform GDPR. Nu colectăm date personale, toate calculele se fac în browser.' }
  ],
  pl: [
    { q: 'Jak oblicza się włoski Kod Podatkowy?', a: 'Kod Podatkowy oblicza się z: nazwiska (3 litery), imienia (3 litery), roku urodzenia (2 cyfry), miesiąca (1 litera), dnia (2 cyfry, +40 dla kobiet), kodu gminy (4 znaki) i znaku kontrolnego.' },
    { q: 'Czy kalkulator online jest darmowy?', a: 'Tak! CodiceFiscale.expert jest całkowicie darmowy, bez limitów i bez rejestracji.' },
    { q: 'Czy wygenerowany Kod Podatkowy jest oficjalny?', a: 'Nasz kalkulator generuje algorytmicznie poprawny Kod Podatkowy zgodnie z zasadami Urzędu Skarbowego. Dla oficjalnego wydania skontaktuj się z włoskim Urzędem Skarbowym.' },
    { q: 'Czym jest omokodia?', a: 'Omokodia występuje gdy dwie osoby mają ten sam obliczony Kod Podatkowy. Urząd Skarbowy rozwiązuje to zastępując niektóre cyfry literami.' },
    { q: 'Jak działa obliczanie dla kobiet?', a: 'Dla kobiet dodaje się 40 do dnia urodzenia. Więc jeśli kobieta urodziła się 15-tego, Kod Podatkowy pokaże 55.' },
    { q: 'Czy moje dane są zapisywane?', a: 'NIE. CodiceFiscale.expert nie zapisuje żadnych danych osobowych. Wszystkie obliczenia odbywają się w twojej przeglądarce.' },
    { q: 'Jak cudzoziemcy uzyskują włoski Kod Podatkowy?', a: 'Cudzoziemcy mogą obliczyć swój Kod Podatkowy wprowadzając dane osobowe i wybierając kraj urodzenia. Dla oficjalnego wydania odwiedź włoski Urząd Skarbowy.' },
    { q: 'Czy strona jest zgodna z RODO?', a: 'Tak, CodiceFiscale.expert jest w pełni zgodny z RODO. Nie zbieramy danych osobowych, wszystkie obliczenia odbywają się w przeglądarce.' }
  ]
};

// Home Page with Calculator
const HomePage = () => {
  const { t, locale } = useI18n();
  const [calculationResult, setCalculationResult] = useState(null);

  const featuresData = HOMEPAGE_FEATURES[locale] || HOMEPAGE_FEATURES.en;
  const features = featuresData.map((f, i) => ({
    ...f,
    icon: [Zap, Lock, Globe, CheckCircle][i]
  }));

  // Extended FAQ for SEO - Long-tail keywords (8 languages)
  const faqs = HOMEPAGE_FAQS[locale] || HOMEPAGE_FAQS.en;

  return (
    <>
      <SEOHead routeKey="home" />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
              {t('hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-emerald-100 mb-8">
              {t('hero.subtitle')}
            </p>
            
            {/* Features */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              {features.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="bg-white/10 backdrop-blur rounded-xl p-4">
                  <Icon className="h-6 w-6 mx-auto mb-2 text-emerald-200" />
                  <p className="font-semibold text-sm">{title}</p>
                  <p className="text-xs text-emerald-200">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-12 bg-gray-50" id="main-content">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <CalculatorForm onResult={setCalculationResult} />
        </div>
      </section>

      {/* FAQ Section - SEO optimized */}
      <section className="py-12 bg-white" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            {locale === 'it' ? 'Domande Frequenti sul Codice Fiscale' : 'Frequently Asked Questions'}
          </h2>
          <div className="space-y-4">
            {faqs.map(({ q, a }, i) => (
              <details 
                key={i} 
                className="bg-gray-50 rounded-xl p-4 group" 
                data-testid={`faq-${i}`}
                itemScope 
                itemProp="mainEntity" 
                itemType="https://schema.org/Question"
              >
                <summary className="font-semibold text-gray-900 cursor-pointer list-none flex justify-between items-center">
                  <span itemProp="name">{q}</span>
                  <span className="text-emerald-600 group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <div 
                  className="mt-3 text-gray-600 leading-relaxed"
                  itemScope 
                  itemProp="acceptedAnswer" 
                  itemType="https://schema.org/Answer"
                >
                  <p itemProp="text">{a}</p>
                </div>
              </details>
            ))}
          </div>
          
          {/* Link to full FAQ */}
          <div className="text-center mt-8">
            <a 
              href="/faq" 
              className="text-emerald-600 hover:text-emerald-700 font-medium hover:underline"
            >
              {locale === 'it' ? 'Vedi tutte le FAQ →' : 'See all FAQs →'}
            </a>
          </div>
        </div>
      </section>

      {/* SEO Content Section - 500+ words */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-emerald max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {locale === 'it' ? 'Calcola il Codice Fiscale Online Gratis' : 'Calculate Italian Tax Code Online Free'}
            </h2>
            {locale === 'it' ? (
              <>
                <p className="text-gray-600 leading-relaxed">
                  <strong>CodiceFiscale.expert</strong> è il calcolatore di Codice Fiscale più veloce e preciso d'Italia. 
                  Inserisci i tuoi dati anagrafici e ottieni istantaneamente il tuo Codice Fiscale italiano. 
                  Il nostro strumento utilizza l'algoritmo ufficiale dell'Agenzia delle Entrate per garantire risultati accurati al 100%.
                </p>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Cos'è il Codice Fiscale?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Il <strong>Codice Fiscale</strong> è un codice alfanumerico di 16 caratteri che identifica in modo univoco 
                  ogni cittadino italiano o straniero residente in Italia. È stato introdotto nel 1973 con il DPR 605 
                  ed è indispensabile per qualsiasi rapporto con la Pubblica Amministrazione, le banche, i datori di lavoro 
                  e il Sistema Sanitario Nazionale.
                </p>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Come si Calcola il Codice Fiscale?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Il calcolo del Codice Fiscale segue un algoritmo preciso stabilito dal Ministero delle Finanze:
                </p>
                <ul className="text-gray-600 mt-2 space-y-1">
                  <li><strong>Caratteri 1-3:</strong> prime tre consonanti del cognome</li>
                  <li><strong>Caratteri 4-6:</strong> prime tre consonanti del nome (con regola speciale per 4+ consonanti)</li>
                  <li><strong>Caratteri 7-8:</strong> ultime due cifre dell'anno di nascita</li>
                  <li><strong>Carattere 9:</strong> lettera corrispondente al mese (A=Gennaio, B=Febbraio, ecc.)</li>
                  <li><strong>Caratteri 10-11:</strong> giorno di nascita (per le donne si aggiunge 40)</li>
                  <li><strong>Caratteri 12-15:</strong> codice catastale del comune di nascita</li>
                  <li><strong>Carattere 16:</strong> carattere di controllo calcolato sui primi 15</li>
                </ul>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">I Nostri Strumenti Gratuiti</h3>
                <p className="text-gray-600 leading-relaxed">
                  Oltre al <strong>calcolo del Codice Fiscale</strong>, offriamo una suite completa di strumenti:
                </p>
                <ul className="text-gray-600 mt-2 space-y-1">
                  <li><strong>Verifica CF:</strong> controlla la validità di un Codice Fiscale esistente</li>
                  <li><strong>Calcolo Inverso:</strong> estrai data di nascita, sesso e comune da un CF</li>
                  <li><strong>Tessera Sanitaria:</strong> genera un'anteprima PDF della tua Tessera Sanitaria</li>
                  <li><strong>Database Comuni:</strong> cerca tra 7904 comuni italiani con codici catastali</li>
                </ul>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Privacy e Sicurezza</h3>
                <p className="text-gray-600 leading-relaxed">
                  La tua privacy è la nostra priorità. <strong>Non salviamo nessun dato personale</strong>. 
                  Tutti i calcoli avvengono direttamente nel tuo browser - nessuna informazione viene mai inviata ai nostri server. 
                  Puoi usare il nostro calcolatore in totale sicurezza.
                </p>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Per Chi è Nato all'Estero</h3>
                <p className="text-gray-600 leading-relaxed">
                  Se sei nato all'estero, il Codice Fiscale utilizza un codice speciale che inizia con "Z" seguito 
                  da tre cifre che identificano lo Stato. Il nostro calcolatore supporta tutti i codici esteri 
                  e calcola correttamente il CF anche per i cittadini stranieri.
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-600 leading-relaxed">
                  <strong>CodiceFiscale.expert</strong> is the fastest and most accurate Italian Tax Code calculator. 
                  Enter your personal data and instantly get your Italian Tax Code (Codice Fiscale). 
                  Our tool uses the official Revenue Agency algorithm to ensure 100% accurate results.
                </p>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">What is the Italian Tax Code?</h3>
                <p className="text-gray-600 leading-relaxed">
                  The <strong>Codice Fiscale</strong> (Italian Tax Code) is a 16-character alphanumeric code that uniquely identifies 
                  every Italian citizen or foreign resident in Italy. Introduced in 1973, it is essential for any interaction 
                  with Italian public administration, banks, employers, and the National Health Service.
                </p>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">How is the Tax Code Calculated?</h3>
                <p className="text-gray-600 leading-relaxed">
                  The Tax Code calculation follows a precise algorithm established by the Ministry of Finance:
                </p>
                <ul className="text-gray-600 mt-2 space-y-1">
                  <li><strong>Characters 1-3:</strong> first three consonants of surname</li>
                  <li><strong>Characters 4-6:</strong> first three consonants of first name (special rule for 4+ consonants)</li>
                  <li><strong>Characters 7-8:</strong> last two digits of birth year</li>
                  <li><strong>Character 9:</strong> letter for month (A=January, B=February, etc.)</li>
                  <li><strong>Characters 10-11:</strong> day of birth (women add 40)</li>
                  <li><strong>Characters 12-15:</strong> cadastral code of birthplace</li>
                  <li><strong>Character 16:</strong> control character calculated from first 15</li>
                </ul>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Our Free Tools</h3>
                <p className="text-gray-600 leading-relaxed">
                  Beyond <strong>Tax Code calculation</strong>, we offer a complete suite of tools:
                </p>
                <ul className="text-gray-600 mt-2 space-y-1">
                  <li><strong>Verify CF:</strong> check the validity of an existing Tax Code</li>
                  <li><strong>Reverse Lookup:</strong> extract birth date, gender and municipality from a CF</li>
                  <li><strong>Health Card:</strong> generate a PDF preview of your Italian Health Card</li>
                  <li><strong>Municipality Database:</strong> search 7904 Italian municipalities with cadastral codes</li>
                </ul>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Privacy and Security</h3>
                <p className="text-gray-600 leading-relaxed">
                  Your privacy is our priority. <strong>We do not save any personal data</strong>.
                  All calculations happen directly in your browser - no information is ever sent to our servers.
                  You can use our calculator with complete confidence.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {locale === 'it' ? 'Chi Siamo' : 'About Us'}
          </h2>
          <div className="prose prose-emerald max-w-none">
            {locale === 'it' ? (
              <>
                <p className="text-gray-600 leading-relaxed">
                  <strong>CodiceFiscale.expert</strong> è nato dalla volontà di offrire un servizio gratuito, veloce e rispettoso della privacy
                  per il calcolo del Codice Fiscale italiano. Siamo un team di sviluppatori italiani appassionati di tecnologia e servizi
                  pubblici digitali.
                </p>
                <p className="text-gray-600 leading-relaxed mt-4">
                  Il nostro obiettivo è semplificare l'accesso agli strumenti fiscali per tutti: cittadini italiani, stranieri residenti in Italia,
                  aziende e professionisti. Crediamo che servizi essenziali come il calcolo del Codice Fiscale debbano essere gratuiti,
                  accessibili e sicuri.
                </p>
                <p className="text-gray-600 leading-relaxed mt-4">
                  Tutti i nostri strumenti sono progettati seguendo le linee guida di accessibilità WCAG 2.1 AA,
                  garantendo un'esperienza ottimale anche per utenti con disabilità. Siamo conformi al GDPR e
                  non raccogliamo né memorizziamo mai dati personali.
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-600 leading-relaxed">
                  <strong>CodiceFiscale.expert</strong> was born from the desire to offer a free, fast, and privacy-respecting service
                  for calculating the Italian Tax Code. We are a team of Italian developers passionate about technology and
                  digital public services.
                </p>
                <p className="text-gray-600 leading-relaxed mt-4">
                  Our goal is to simplify access to tax tools for everyone: Italian citizens, foreign residents in Italy,
                  businesses, and professionals. We believe that essential services like Tax Code calculation should be free,
                  accessible, and secure.
                </p>
                <p className="text-gray-600 leading-relaxed mt-4">
                  All our tools are designed following WCAG 2.1 AA accessibility guidelines,
                  ensuring an optimal experience for users with disabilities. We are GDPR compliant and
                  never collect or store personal data.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {locale === 'it' ? 'Guide e Approfondimenti' : 'Guides and Resources'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a href={locale === 'it' ? '/blog/come-calcolare-codice-fiscale' : '/en/blog/how-to-calculate-tax-code'}
               className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">
                {locale === 'it' ? 'Come Calcolare il Codice Fiscale' : 'How to Calculate Tax Code'}
              </h3>
              <p className="text-sm text-gray-600">
                {locale === 'it' ? 'Guida completa passo passo con esempi pratici' : 'Complete step-by-step guide with examples'}
              </p>
            </a>
            <a href={locale === 'it' ? '/blog/omocodia-spiegata' : '/en/blog/omocodia-explained'}
               className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">
                {locale === 'it' ? "L'Omocodia Spiegata" : 'Omocodia Explained'}
              </h3>
              <p className="text-sm text-gray-600">
                {locale === 'it' ? "Cos'è e come viene risolta dall'Agenzia delle Entrate" : 'What it is and how the Revenue Agency resolves it'}
              </p>
            </a>
            <a href={locale === 'it' ? '/blog/codice-fiscale-stranieri' : '/en/blog/tax-code-foreigners'}
               className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">
                {locale === 'it' ? 'Codice Fiscale per Stranieri' : 'Tax Code for Foreigners'}
              </h3>
              <p className="text-sm text-gray-600">
                {locale === 'it' ? 'Come ottenerlo se sei cittadino non italiano' : 'How to get it if you are not Italian'}
              </p>
            </a>
          </div>
          <div className="text-center mt-8">
            <a href={locale === 'it' ? '/blog' : '/en/blog'}
               className="inline-flex items-center text-emerald-600 hover:text-emerald-700 font-medium">
              {locale === 'it' ? 'Vedi tutte le guide →' : 'See all guides →'}
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

// Reverse Page - Enhanced with educational content
const REVERSE_CONTENT = {
  it: {
    title: "Codice Fiscale Inverso",
    subtitle: "Decodifica un Codice Fiscale esistente per scoprire i dati anagrafici",
    intro: "Il servizio di decodifica inversa del Codice Fiscale ti permette di estrarre le informazioni anagrafiche contenute in un CF esistente. Inserisci i 16 caratteri e scopri immediatamente sesso, data di nascita e luogo di nascita della persona.",
    howItWorks: "Come Funziona",
    steps: [
      "Inserisci il Codice Fiscale completo di 16 caratteri",
      "Il sistema analizza automaticamente ogni sezione del codice",
      "Vengono estratti: sesso, data di nascita, comune/stato di nascita",
      "Se presente omocodia, viene rilevata automaticamente"
    ],
    whatYouGet: "Cosa Puoi Scoprire",
    results: [
      "Sesso (maschio/femmina) dalla cifra del giorno",
      "Data di nascita completa (giorno, mese, anno)",
      "Comune di nascita italiano o stato estero",
      "Livello di omocodia (se presente)"
    ],
    decodeTitle: "Come Decodificare un Codice Fiscale a Mano",
    decodeIntro: "Lo strumento sopra fa il lavoro in un istante, ma sapere cosa succede dietro le quinte serve a due cose concrete: capire perché il nome e il cognome non tornano mai per intero, e riconoscere a colpo d'occhio un codice palesemente inventato. Il procedimento è quello di generazione letto al contrario, posizione per posizione.",
    decodeSteps: [
      {
        title: "Sesso e giorno di nascita (posizioni 10-11)",
        text: "Guarda le due cifre in decima e undicesima posizione. Da 01 a 31 è un uomo, e il numero è il giorno di nascita così com'è. Da 41 a 71 è una donna: sottrai 40 e ottieni il giorno vero. Esempio: \"55\" meno 40 fa 15, quindi persona di sesso femminile nata il giorno 15."
      },
      {
        title: "Mese di nascita (posizione 9)",
        text: "La nona posizione è sempre una lettera, mai una cifra, e segue una tabella fissa che salta apposta le lettere facili da confondere con i numeri: A=gennaio, B=febbraio, C=marzo, D=aprile, E=maggio, H=giugno, L=luglio, M=agosto, P=settembre, R=ottobre, S=novembre, T=dicembre."
      },
      {
        title: "Anno di nascita (posizioni 7-8) — il limite che pochi dichiarano",
        text: "Le posizioni 7 e 8 riportano solo le ultime due cifre dell'anno: \"85\" può significare 1985 o 2085, stesso identico codice. Un decodificatore stima il secolo confrontando quel numero con l'anno corrente — se è maggiore delle ultime due cifre dell'anno in corso assume il 1900, altrimenti il 2000. È una stima quasi sempre corretta, ma resta una stima: il secolo non è mai scritto nel codice in modo esplicito. Se conosci già l'età approssimativa della persona, fidati di quella più che del calcolo automatico."
      },
      {
        title: "Comune o stato di nascita (posizioni 12-15)",
        text: "Le ultime quattro posizioni prima del carattere di controllo sono il codice catastale, detto anche codice Belfiore: una lettera più tre cifre per i comuni italiani (Milano è F205, Roma è H501), oppure la lettera Z più tre cifre per chi è nato all'estero. Questo codice non si calcola con una formula: è un elenco fisso mantenuto dall'Agenzia delle Entrate su base ISTAT, quindi per la conversione serve una tabella di consultazione."
      },
      {
        title: "Cognome e nome (posizioni 1-6) — dove la decodifica si ferma",
        text: "Le prime tre lettere derivano dalle consonanti del cognome (completate con le vocali se non bastano), le successive tre dal nome con una regola leggermente diversa quando il nome ha quattro o più consonanti. L'operazione perde informazione per costruzione: \"ROSSI\" diventa RSS, ma altri cognomi con le stesse consonanti nello stesso ordine produrrebbero le identiche tre lettere. Da sole, non permettono di risalire con certezza al cognome originale — solo di verificare se un nome noto è compatibile con quelle lettere."
      }
    ],
    omocodiaSectionTitle: "Cosa Fare se Compaiono Lettere al Posto di Cifre",
    omocodiaSectionText: "Le posizioni che dovrebbero contenere solo cifre — anno, giorno, ultime tre del comune — a volte mostrano una lettera. Non è un errore di trascrizione: è omocodia, la soluzione con cui l'Agenzia delle Entrate evita di assegnare lo stesso codice a due persone diverse. Prima di leggere quei dati devi riconvertire la lettera nella cifra originale, con la tabella inversa di quella usata in fase di generazione:",
    omocodiaTable: [["L","0"],["M","1"],["N","2"],["P","3"],["Q","4"],["R","5"],["S","6"],["T","7"],["U","8"],["V","9"]],
    omocodiaSectionNote: "Applica la sostituzione solo dove ti aspetti una cifra, poi procedi con i passaggi visti sopra come se il codice fosse standard. Un dettaglio da non perdere: la sostituzione parte sempre dalla posizione 15 e risale verso sinistra, quindi in un codice con omocodia multipla può comparire più di una lettera fuori posto.",
    exampleTitle: "Un Esempio Completo, Letto alla Rovescia",
    exampleIntro: "Prendiamo un codice fiscale valido e leggiamolo posizione per posizione, esattamente come faresti a mano.",
    example: {
      cf: "VRDNNA85R55F205K",
      breakdown: [
        "VRD → cognome: consonanti compatibili con un cognome come Verdi",
        "NNA → nome: consonanti e vocale compatibili con un nome come Anna",
        "85 → anno: confrontato con l'anno corrente, il calcolo assume 1985",
        "R → mese: ottobre, dalla tabella dei mesi",
        "55 → giorno e sesso: 55 meno 40 fa 15, quindi sesso femminile, nata il 15",
        "F205 → comune: Milano, codice catastale F205",
        "K → carattere di controllo, ricalcolato sui 15 caratteri precedenti"
      ],
      result: "Risultato della lettura: persona di sesso femminile, nata il 15 ottobre 1985 a Milano. Di cognome e nome sappiamo solo che sono compatibili con le lettere VRD e NNA — potrebbe trattarsi di Verdi Anna come di migliaia di altre combinazioni con le stesse consonanti."
    },
    faq: [
      { q: "Posso risalire al nome e cognome?", a: "No, il CF contiene solo 3 lettere per cognome e 3 per nome. Non è possibile risalire al nome completo, solo verificare la compatibilità con un nome noto." },
      { q: "Funziona con codici in omocodia?", a: "Sì! Il nostro sistema riconosce automaticamente i codici con omocodia e li decodifica correttamente." },
      { q: "È legale decodificare un CF?", a: "Sì, la decodifica è legale. Il CF è un dato pubblico presente su molti documenti. Non richiede consenso per la sola decodifica." },
      { q: "Cos'è il calcolo del codice fiscale inverso?", a: "È il procedimento contrario a quello di generazione: invece di partire dai dati anagrafici per ottenere il codice, si parte dal codice per risalire a sesso, data di nascita e comune. Non è un calcolo nel senso di una formula da risolvere, ma una lettura posizione per posizione secondo le stesse regole usate in fase di generazione, applicate al contrario." },
      { q: "Posso decodificare un codice fiscale senza strumenti online?", a: "Sì, a mano: bastano la tabella dei mesi e quella dei codici catastali, più i passaggi descritti in questa pagina. Per un uso occasionale resta più comodo uno strumento come questo, che fa gli stessi passaggi in automatico e riconosce da solo l'eventuale omocodia." },
      { q: "Perché due decodifiche danno anni di nascita diversi per lo stesso codice?", a: "Perché l'anno di nascita nel codice fiscale è scritto con due sole cifre e il secolo non è mai indicato esplicitamente. Strumenti diversi possono stimare il secolo in modo diverso; se conosci l'età approssimativa della persona, quella resta l'informazione più affidabile." },
      { q: "\"Fiscale inverso\" e \"codice fiscale inverso\" indicano la stessa cosa?", a: "Sì, sono varianti abbreviate della stessa ricerca: decodificare un codice fiscale esistente per ottenere sesso, data e luogo di nascita di chi lo possiede." }
    ],
    relatedTitle: "Strumenti Correlati"
  },
  en: {
    title: "Reverse Tax Code Lookup",
    subtitle: "Decode an existing Tax Code to discover personal information",
    intro: "The reverse Tax Code lookup service allows you to extract personal information contained in an existing CF. Enter the 16 characters and instantly discover the gender, birth date, and birthplace of the person.",
    howItWorks: "How It Works",
    steps: [
      "Enter the complete 16-character Tax Code",
      "The system automatically analyzes each section",
      "Extracted: gender, birth date, municipality/country of birth",
      "Omocodia is automatically detected if present"
    ],
    whatYouGet: "What You Can Discover",
    results: [
      "Gender (male/female) from the day digit",
      "Complete birth date (day, month, year)",
      "Italian municipality or foreign country of birth",
      "Omocodia level (if present)"
    ],
    faq: [
      { q: "Can I find the full name?", a: "No, the CF contains only 3 letters for surname and 3 for first name. You cannot find the complete name, only verify compatibility with a known name." },
      { q: "Does it work with omocodia codes?", a: "Yes! Our system automatically recognizes codes with omocodia and decodes them correctly." },
      { q: "Is it legal to decode a CF?", a: "Yes, decoding is legal. The CF is public data present on many documents. Consent is not required for decoding alone." }
    ],
    relatedTitle: "Related Tools"
  },
  de: {
    title: "Umgekehrte Steuerkennzeichen-Suche",
    subtitle: "Dekodieren Sie ein vorhandenes Steuerkennzeichen, um persönliche Daten zu ermitteln",
    intro: "Der umgekehrte Steuerkennzeichen-Suchdienst ermöglicht es Ihnen, persönliche Informationen aus einem vorhandenen CF zu extrahieren. Geben Sie die 16 Zeichen ein und ermitteln Sie sofort Geschlecht, Geburtsdatum und Geburtsort.",
    howItWorks: "So funktioniert es",
    steps: [
      "Geben Sie das vollständige 16-stellige Steuerkennzeichen ein",
      "Das System analysiert automatisch jeden Abschnitt",
      "Extrahiert: Geschlecht, Geburtsdatum, Gemeinde/Land",
      "Omocodia wird automatisch erkannt"
    ],
    whatYouGet: "Was Sie herausfinden können",
    results: [
      "Geschlecht (männlich/weiblich) aus der Tagesziffer",
      "Vollständiges Geburtsdatum (Tag, Monat, Jahr)",
      "Italienische Gemeinde oder ausländisches Land",
      "Omocodia-Stufe (falls vorhanden)"
    ],
    faq: [
      { q: "Kann ich den vollständigen Namen finden?", a: "Nein, das CF enthält nur 3 Buchstaben für Nachname und 3 für Vorname. Der vollständige Name kann nicht ermittelt werden." },
      { q: "Funktioniert es mit Omocodia-Codes?", a: "Ja! Unser System erkennt automatisch Codes mit Omocodia und dekodiert sie korrekt." },
      { q: "Ist es legal, ein CF zu dekodieren?", a: "Ja, das Dekodieren ist legal. Das CF ist ein öffentliches Datum auf vielen Dokumenten." }
    ],
    relatedTitle: "Verwandte Tools"
  },
  fr: {
    title: "Recherche Inverse du Code Fiscal",
    subtitle: "Décodez un Code Fiscal existant pour découvrir les informations personnelles",
    intro: "Le service de recherche inverse du Code Fiscal vous permet d'extraire les informations personnelles contenues dans un CF existant. Entrez les 16 caractères et découvrez instantanément le sexe, la date de naissance et le lieu de naissance.",
    howItWorks: "Comment ça marche",
    steps: [
      "Entrez le Code Fiscal complet de 16 caractères",
      "Le système analyse automatiquement chaque section",
      "Extraits: sexe, date de naissance, commune/pays de naissance",
      "L'omocodia est automatiquement détectée"
    ],
    whatYouGet: "Ce que vous pouvez découvrir",
    results: [
      "Sexe (homme/femme) à partir du chiffre du jour",
      "Date de naissance complète (jour, mois, année)",
      "Commune italienne ou pays étranger de naissance",
      "Niveau d'omocodia (si présent)"
    ],
    faq: [
      { q: "Puis-je trouver le nom complet?", a: "Non, le CF ne contient que 3 lettres pour le nom de famille et 3 pour le prénom. Vous ne pouvez pas trouver le nom complet." },
      { q: "Ça fonctionne avec les codes omocodia?", a: "Oui! Notre système reconnaît automatiquement les codes avec omocodia et les décode correctement." },
      { q: "Est-ce légal de décoder un CF?", a: "Oui, le décodage est légal. Le CF est une donnée publique présente sur de nombreux documents." }
    ],
    relatedTitle: "Outils connexes"
  },
  es: {
    title: "Búsqueda Inversa del Código Fiscal",
    subtitle: "Decodifica un Código Fiscal existente para descubrir información personal",
    intro: "El servicio de búsqueda inversa del Código Fiscal te permite extraer la información personal contenida en un CF existente. Ingresa los 16 caracteres y descubre instantáneamente el sexo, fecha de nacimiento y lugar de nacimiento.",
    howItWorks: "Cómo funciona",
    steps: [
      "Ingresa el Código Fiscal completo de 16 caracteres",
      "El sistema analiza automáticamente cada sección",
      "Se extrae: sexo, fecha de nacimiento, municipio/país de nacimiento",
      "La omocodia se detecta automáticamente"
    ],
    whatYouGet: "Qué puedes descubrir",
    results: [
      "Sexo (masculino/femenino) del dígito del día",
      "Fecha de nacimiento completa (día, mes, año)",
      "Municipio italiano o país extranjero de nacimiento",
      "Nivel de omocodia (si está presente)"
    ],
    faq: [
      { q: "¿Puedo encontrar el nombre completo?", a: "No, el CF solo contiene 3 letras para el apellido y 3 para el nombre. No puedes encontrar el nombre completo." },
      { q: "¿Funciona con códigos de omocodia?", a: "¡Sí! Nuestro sistema reconoce automáticamente los códigos con omocodia y los decodifica correctamente." },
      { q: "¿Es legal decodificar un CF?", a: "Sí, la decodificación es legal. El CF es un dato público presente en muchos documentos." }
    ],
    relatedTitle: "Herramientas relacionadas"
  },
  pt: {
    title: "Pesquisa Inversa do Código Fiscal",
    subtitle: "Decodifique um Código Fiscal existente para descobrir informações pessoais",
    intro: "O serviço de pesquisa inversa do Código Fiscal permite extrair as informações pessoais contidas em um CF existente. Insira os 16 caracteres e descubra instantaneamente o sexo, data de nascimento e local de nascimento.",
    howItWorks: "Como funciona",
    steps: [
      "Insira o Código Fiscal completo de 16 caracteres",
      "O sistema analisa automaticamente cada seção",
      "Extraído: sexo, data de nascimento, município/país de nascimento",
      "A omocodia é detectada automaticamente"
    ],
    whatYouGet: "O que você pode descobrir",
    results: [
      "Sexo (masculino/feminino) do dígito do dia",
      "Data de nascimento completa (dia, mês, ano)",
      "Município italiano ou país estrangeiro de nascimento",
      "Nível de omocodia (se presente)"
    ],
    faq: [
      { q: "Posso encontrar o nome completo?", a: "Não, o CF contém apenas 3 letras para sobrenome e 3 para nome. Você não pode encontrar o nome completo." },
      { q: "Funciona com códigos de omocodia?", a: "Sim! Nosso sistema reconhece automaticamente os códigos com omocodia e os decodifica corretamente." },
      { q: "É legal decodificar um CF?", a: "Sim, a decodificação é legal. O CF é um dado público presente em muitos documentos." }
    ],
    relatedTitle: "Ferramentas relacionadas"
  },
  ro: {
    title: "Căutare Inversă Cod Fiscal",
    subtitle: "Decodificați un Cod Fiscal existent pentru a descoperi informații personale",
    intro: "Serviciul de căutare inversă a Codului Fiscal vă permite să extrageți informațiile personale conținute într-un CF existent. Introduceți cele 16 caractere și descoperiți instantaneu sexul, data nașterii și locul nașterii.",
    howItWorks: "Cum funcționează",
    steps: [
      "Introduceți Codul Fiscal complet de 16 caractere",
      "Sistemul analizează automat fiecare secțiune",
      "Extrase: sex, data nașterii, municipiu/țară de naștere",
      "Omocodia este detectată automat"
    ],
    whatYouGet: "Ce puteți descoperi",
    results: [
      "Sex (masculin/feminin) din cifra zilei",
      "Data nașterii completă (zi, lună, an)",
      "Municipiu italian sau țară străină de naștere",
      "Nivelul de omocodie (dacă este prezent)"
    ],
    faq: [
      { q: "Pot găsi numele complet?", a: "Nu, CF conține doar 3 litere pentru numele de familie și 3 pentru prenume. Nu puteți găsi numele complet." },
      { q: "Funcționează cu coduri de omocodie?", a: "Da! Sistemul nostru recunoaște automat codurile cu omocodie și le decodifică corect." },
      { q: "Este legal să decodific un CF?", a: "Da, decodificarea este legală. CF este o dată publică prezentă pe multe documente." }
    ],
    relatedTitle: "Instrumente conexe"
  },
  pl: {
    title: "Odwrotne Wyszukiwanie Kodu Podatkowego",
    subtitle: "Dekoduj istniejący Kod Podatkowy, aby odkryć dane osobowe",
    intro: "Usługa odwrotnego wyszukiwania Kodu Podatkowego pozwala wyodrębnić dane osobowe zawarte w istniejącym CF. Wprowadź 16 znaków i natychmiast odkryj płeć, datę urodzenia i miejsce urodzenia.",
    howItWorks: "Jak to działa",
    steps: [
      "Wprowadź pełny 16-znakowy Kod Podatkowy",
      "System automatycznie analizuje każdą sekcję",
      "Wyodrębnione: płeć, data urodzenia, gmina/kraj urodzenia",
      "Omocodia jest wykrywana automatycznie"
    ],
    whatYouGet: "Co możesz odkryć",
    results: [
      "Płeć (mężczyzna/kobieta) z cyfry dnia",
      "Pełna data urodzenia (dzień, miesiąc, rok)",
      "Włoska gmina lub zagraniczny kraj urodzenia",
      "Poziom omocodii (jeśli obecny)"
    ],
    faq: [
      { q: "Czy mogę znaleźć pełne imię?", a: "Nie, CF zawiera tylko 3 litery nazwiska i 3 imienia. Nie możesz znaleźć pełnego imienia." },
      { q: "Czy działa z kodami omocodia?", a: "Tak! Nasz system automatycznie rozpoznaje kody z omocodią i dekoduje je poprawnie." },
      { q: "Czy dekodowanie CF jest legalne?", a: "Tak, dekodowanie jest legalne. CF to dane publiczne obecne na wielu dokumentach." }
    ],
    relatedTitle: "Powiązane narzędzia"
  }
};

const ReversePage = () => {
  const { locale } = useI18n();
  const content = REVERSE_CONTENT[locale] || REVERSE_CONTENT.en;
  const { Link } = require('react-router-dom');

  return (
    <>
      <SEOHead routeKey="reverse" />
      <section className="py-12 bg-gray-50" id="main-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{content.title}</h1>
            <p className="text-lg text-gray-600">{content.subtitle}</p>
          </div>

          {/* Intro */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
            <p className="text-gray-600 leading-relaxed">{content.intro}</p>
          </div>

          {/* Form */}
          <div className="mb-8">
            <ReverseForm />
          </div>

          {/* How It Works */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{content.howItWorks}</h2>
            <ol className="space-y-3">
              {content.steps.map((step, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold">{idx + 1}</span>
                  <span className="text-gray-600">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* What You Get */}
          <div className="bg-emerald-50 rounded-2xl p-6 mb-8 border border-emerald-200">
            <h2 className="text-xl font-bold text-emerald-900 mb-4">{content.whatYouGet}</h2>
            <ul className="space-y-2">
              {content.results.map((result, idx) => (
                <li key={idx} className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  {result}
                </li>
              ))}
            </ul>
          </div>

          {/* Manual Decoding Guide */}
          {content.decodeSteps && (
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-2">{content.decodeTitle}</h2>
              <p className="text-gray-600 leading-relaxed mb-6">{content.decodeIntro}</p>
              <div className="space-y-4">
                {content.decodeSteps.map((step, idx) => (
                  <div key={idx} className="border-l-4 border-emerald-500 pl-4 py-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Omocodia reverse table */}
          {content.omocodiaTable && (
            <div className="bg-amber-50 rounded-2xl p-6 mb-8 border border-amber-200">
              <h2 className="text-xl font-bold text-amber-900 mb-3">{content.omocodiaSectionTitle}</h2>
              <p className="text-amber-800 leading-relaxed mb-4">{content.omocodiaSectionText}</p>
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full border-collapse text-center bg-white rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-amber-100">
                      {content.omocodiaTable.map(([letter], idx) => (
                        <th key={idx} className="border border-amber-200 p-2 font-mono">{letter}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {content.omocodiaTable.map(([, digit], idx) => (
                        <td key={idx} className="border border-amber-200 p-2 font-mono font-bold text-amber-700">{digit}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-amber-800 leading-relaxed">{content.omocodiaSectionNote}</p>
            </div>
          )}

          {/* Worked Example */}
          {content.example && (
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-2">{content.exampleTitle}</h2>
              <p className="text-gray-600 leading-relaxed mb-4">{content.exampleIntro}</p>
              <p className="text-center font-mono text-2xl tracking-wider bg-gray-50 rounded-xl p-4 mb-4">{content.example.cf}</p>
              <ul className="space-y-2 mb-4">
                {content.example.breakdown.map((line, idx) => (
                  <li key={idx} className="font-mono text-sm bg-gray-50 rounded-lg p-3 text-gray-700">{line}</li>
                ))}
              </ul>
              <p className="text-gray-700 leading-relaxed font-medium">{content.example.result}</p>
            </div>
          )}

          {/* Related in-depth articles */}
          {content.decodeSteps && locale === 'it' && (
            <div className="bg-gray-50 rounded-2xl p-6 mb-8 border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Per Approfondire</h2>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <Link to={getArticleUrl('struttura-codice-fiscale', locale)} className="text-emerald-600 hover:underline font-medium">Struttura completa del codice fiscale</Link>
                  {' '}— tutte le regole su cognome, nome e le eccezioni sui casi limite.
                </li>
                <li>
                  <Link to={getArticleUrl('omocodia-spiegata', locale)} className="text-emerald-600 hover:underline font-medium">Omocodia spiegata</Link>
                  {' '}— i sette livelli e perché l'Agenzia delle Entrate sostituisce le cifre.
                </li>
                <li>
                  <Link to={getArticleUrl('codici-catastali-comuni', locale)} className="text-emerald-600 hover:underline font-medium">Codici catastali dei comuni</Link>
                  {' '}— l'elenco per convertire le ultime quattro posizioni in un luogo di nascita.
                </li>
                <li>
                  <Link to={getArticleUrl('carattere-controllo-cin', locale)} className="text-emerald-600 hover:underline font-medium">Carattere di controllo CIN</Link>
                  {' '}— come si ricalcola l'ultima lettera e perché cambia con l'omocodia.
                </li>
              </ul>
            </div>
          )}

          {/* FAQ */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">FAQ</h2>
            <div className="space-y-4">
              {content.faq.map((item, idx) => (
                <div key={idx} className="border-b border-gray-200 pb-4 last:border-0">
                  <h3 className="font-semibold text-gray-900 mb-2">{item.q}</h3>
                  <p className="text-gray-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Related Tools */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white text-center">
            <h2 className="text-xl font-bold mb-4">{content.relatedTitle}</h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={locale === 'it' ? '/' : `/${locale}`} className="bg-white text-emerald-700 px-4 py-2 rounded-lg font-semibold hover:bg-emerald-50">{locale === 'it' ? 'Calcola CF' : 'Calculate'}</a>
              <a href={locale === 'it' ? '/verifica' : `/${locale}/verify`} className="bg-white/20 text-white px-4 py-2 rounded-lg font-semibold hover:bg-white/30">{locale === 'it' ? 'Verifica CF' : 'Verify'}</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// Verify Page - Enhanced with educational content
const VERIFY_CONTENT = {
  it: {
    title: "Verifica Codice Fiscale",
    subtitle: "Controlla istantaneamente la validità di un Codice Fiscale italiano",
    intro: "Il verificatore di Codice Fiscale controlla automaticamente la correttezza formale del codice inserito. Verifica il formato, il carattere di controllo CIN, la coerenza delle date e riconosce automaticamente i codici con omocodia.",
    whatWeCheck: "Cosa Verifichiamo",
    checks: [
      "Lunghezza corretta (16 caratteri)",
      "Formato valido (lettere e numeri nelle posizioni corrette)",
      "Carattere di controllo CIN corretto",
      "Coerenza del giorno di nascita (1-31 o 41-71 per donne)",
      "Mese di nascita valido (A-T escludendo lettere non usate)",
      "Rilevamento automatico omocodia"
    ],
    important: "Importante",
    disclaimer: "Questo strumento verifica la correttezza FORMALE del codice fiscale. Non può confermare se il CF è effettivamente associato a una persona reale. Per verifiche ufficiali, contattare l'Agenzia delle Entrate.",
    faq: [
      { q: "Cosa significa CIN non valido?", a: "Il CIN (carattere di controllo) viene calcolato sui primi 15 caratteri. Se non corrisponde, il codice contiene un errore di trascrizione." },
      { q: "Verifica anche i codici con omocodia?", a: "Sì! Il sistema riconosce automaticamente i codici con omocodia e li verifica correttamente, anche con multiple sostituzioni." },
      { q: "Posso verificare se un CF esiste davvero?", a: "No, solo l'Agenzia delle Entrate può confermare l'associazione tra CF e persona fisica. Noi verifichiamo solo la correttezza formale." }
    ]
  },
  en: {
    title: "Verify Tax Code",
    subtitle: "Instantly check the validity of an Italian Tax Code",
    intro: "The Tax Code verifier automatically checks the formal correctness of the entered code. It verifies the format, CIN check character, date consistency and automatically recognizes codes with omocodia.",
    whatWeCheck: "What We Check",
    checks: [
      "Correct length (16 characters)",
      "Valid format (letters and numbers in correct positions)",
      "Correct CIN check character",
      "Birth day consistency (1-31 or 41-71 for women)",
      "Valid birth month (A-T excluding unused letters)",
      "Automatic omocodia detection"
    ],
    important: "Important",
    disclaimer: "This tool verifies the FORMAL correctness of the tax code. It cannot confirm if the CF is actually associated with a real person. For official verification, contact the Revenue Agency.",
    faq: [
      { q: "What does invalid CIN mean?", a: "The CIN (check character) is calculated on the first 15 characters. If it doesn't match, the code contains a transcription error." },
      { q: "Does it verify omocodia codes?", a: "Yes! The system automatically recognizes codes with omocodia and verifies them correctly, even with multiple substitutions." },
      { q: "Can I verify if a CF really exists?", a: "No, only the Revenue Agency can confirm the association between CF and a real person. We only verify formal correctness." }
    ]
  },
  de: {
    title: "Steuerkennzeichen Überprüfen",
    subtitle: "Überprüfen Sie sofort die Gültigkeit eines italienischen Steuerkennzeichens",
    intro: "Der Steuerkennzeichen-Prüfer überprüft automatisch die formale Korrektheit des eingegebenen Codes. Er überprüft Format, CIN-Prüfzeichen, Datumskonsistenz und erkennt automatisch Codes mit Omocodia.",
    whatWeCheck: "Was wir prüfen",
    checks: [
      "Korrekte Länge (16 Zeichen)",
      "Gültiges Format (Buchstaben und Zahlen an korrekten Positionen)",
      "Korrektes CIN-Prüfzeichen",
      "Konsistenz des Geburtstags (1-31 oder 41-71 für Frauen)",
      "Gültiger Geburtsmonat (A-T ohne unbenutzte Buchstaben)",
      "Automatische Omocodia-Erkennung"
    ],
    important: "Wichtig",
    disclaimer: "Dieses Tool überprüft die FORMALE Korrektheit des Steuerkennzeichens. Es kann nicht bestätigen, ob das CF tatsächlich einer realen Person zugeordnet ist.",
    faq: [
      { q: "Was bedeutet ungültiges CIN?", a: "Das CIN wird aus den ersten 15 Zeichen berechnet. Wenn es nicht übereinstimmt, enthält der Code einen Übertragungsfehler." },
      { q: "Überprüft es auch Omocodia-Codes?", a: "Ja! Das System erkennt automatisch Codes mit Omocodia und überprüft sie korrekt." },
      { q: "Kann ich überprüfen, ob ein CF wirklich existiert?", a: "Nein, nur die Steuerbehörde kann die Zuordnung bestätigen. Wir überprüfen nur die formale Korrektheit." }
    ]
  },
  fr: {
    title: "Vérifier le Code Fiscal",
    subtitle: "Vérifiez instantanément la validité d'un Code Fiscal italien",
    intro: "Le vérificateur de Code Fiscal vérifie automatiquement la correction formelle du code saisi. Il vérifie le format, le caractère de contrôle CIN, la cohérence des dates et reconnaît automatiquement les codes avec omocodia.",
    whatWeCheck: "Ce que nous vérifions",
    checks: [
      "Longueur correcte (16 caractères)",
      "Format valide (lettres et chiffres aux positions correctes)",
      "Caractère de contrôle CIN correct",
      "Cohérence du jour de naissance (1-31 ou 41-71 pour les femmes)",
      "Mois de naissance valide (A-T excluant les lettres non utilisées)",
      "Détection automatique de l'omocodia"
    ],
    important: "Important",
    disclaimer: "Cet outil vérifie la correction FORMELLE du code fiscal. Il ne peut pas confirmer si le CF est réellement associé à une personne réelle.",
    faq: [
      { q: "Que signifie CIN invalide?", a: "Le CIN est calculé sur les 15 premiers caractères. S'il ne correspond pas, le code contient une erreur de transcription." },
      { q: "Vérifie-t-il les codes omocodia?", a: "Oui! Le système reconnaît automatiquement les codes avec omocodia et les vérifie correctement." },
      { q: "Puis-je vérifier si un CF existe vraiment?", a: "Non, seule l'Agence des Revenus peut confirmer l'association. Nous vérifions uniquement la correction formelle." }
    ]
  },
  es: {
    title: "Verificar Código Fiscal",
    subtitle: "Verifica instantáneamente la validez de un Código Fiscal italiano",
    intro: "El verificador de Código Fiscal comprueba automáticamente la corrección formal del código ingresado. Verifica el formato, el carácter de control CIN, la consistencia de fechas y reconoce automáticamente los códigos con omocodia.",
    whatWeCheck: "Qué verificamos",
    checks: [
      "Longitud correcta (16 caracteres)",
      "Formato válido (letras y números en posiciones correctas)",
      "Carácter de control CIN correcto",
      "Consistencia del día de nacimiento (1-31 o 41-71 para mujeres)",
      "Mes de nacimiento válido (A-T excluyendo letras no usadas)",
      "Detección automática de omocodia"
    ],
    important: "Importante",
    disclaimer: "Esta herramienta verifica la corrección FORMAL del código fiscal. No puede confirmar si el CF está realmente asociado a una persona real.",
    faq: [
      { q: "¿Qué significa CIN inválido?", a: "El CIN se calcula sobre los primeros 15 caracteres. Si no coincide, el código contiene un error de transcripción." },
      { q: "¿Verifica códigos de omocodia?", a: "¡Sí! El sistema reconoce automáticamente los códigos con omocodia y los verifica correctamente." },
      { q: "¿Puedo verificar si un CF realmente existe?", a: "No, solo la Agencia Tributaria puede confirmar la asociación. Solo verificamos la corrección formal." }
    ]
  },
  pt: {
    title: "Verificar Código Fiscal",
    subtitle: "Verifique instantaneamente a validade de um Código Fiscal italiano",
    intro: "O verificador de Código Fiscal verifica automaticamente a correção formal do código inserido. Verifica o formato, o caractere de controle CIN, a consistência de datas e reconhece automaticamente os códigos com omocodia.",
    whatWeCheck: "O que verificamos",
    checks: [
      "Comprimento correto (16 caracteres)",
      "Formato válido (letras e números nas posições corretas)",
      "Caractere de controle CIN correto",
      "Consistência do dia de nascimento (1-31 ou 41-71 para mulheres)",
      "Mês de nascimento válido (A-T excluindo letras não usadas)",
      "Detecção automática de omocodia"
    ],
    important: "Importante",
    disclaimer: "Esta ferramenta verifica a correção FORMAL do código fiscal. Não pode confirmar se o CF está realmente associado a uma pessoa real.",
    faq: [
      { q: "O que significa CIN inválido?", a: "O CIN é calculado sobre os primeiros 15 caracteres. Se não corresponder, o código contém um erro de transcrição." },
      { q: "Verifica códigos de omocodia?", a: "Sim! O sistema reconhece automaticamente os códigos com omocodia e os verifica corretamente." },
      { q: "Posso verificar se um CF realmente existe?", a: "Não, apenas a Agência da Receita pode confirmar a associação. Apenas verificamos a correção formal." }
    ]
  },
  ro: {
    title: "Verifică Codul Fiscal",
    subtitle: "Verificați instantaneu validitatea unui Cod Fiscal italian",
    intro: "Verificatorul de Cod Fiscal verifică automat corectitudinea formală a codului introdus. Verifică formatul, caracterul de control CIN, consistența datelor și recunoaște automat codurile cu omocodie.",
    whatWeCheck: "Ce verificăm",
    checks: [
      "Lungime corectă (16 caractere)",
      "Format valid (litere și cifre în pozițiile corecte)",
      "Caracter de control CIN corect",
      "Consistența zilei de naștere (1-31 sau 41-71 pentru femei)",
      "Lună de naștere validă (A-T excluzând literele neutilizate)",
      "Detectare automată a omocodiei"
    ],
    important: "Important",
    disclaimer: "Acest instrument verifică corectitudinea FORMALĂ a codului fiscal. Nu poate confirma dacă CF este efectiv asociat unei persoane reale.",
    faq: [
      { q: "Ce înseamnă CIN invalid?", a: "CIN-ul este calculat pe primele 15 caractere. Dacă nu se potrivește, codul conține o eroare de transcriere." },
      { q: "Verifică și codurile de omocodie?", a: "Da! Sistemul recunoaște automat codurile cu omocodie și le verifică corect." },
      { q: "Pot verifica dacă un CF există cu adevărat?", a: "Nu, doar Agenția Fiscală poate confirma asocierea. Verificăm doar corectitudinea formală." }
    ]
  },
  pl: {
    title: "Zweryfikuj Kod Podatkowy",
    subtitle: "Natychmiast sprawdź poprawność włoskiego Kodu Podatkowego",
    intro: "Weryfikator Kodu Podatkowego automatycznie sprawdza formalną poprawność wprowadzonego kodu. Weryfikuje format, znak kontrolny CIN, spójność dat i automatycznie rozpoznaje kody z omocodią.",
    whatWeCheck: "Co sprawdzamy",
    checks: [
      "Prawidłowa długość (16 znaków)",
      "Prawidłowy format (litery i cyfry na właściwych pozycjach)",
      "Prawidłowy znak kontrolny CIN",
      "Spójność dnia urodzenia (1-31 lub 41-71 dla kobiet)",
      "Prawidłowy miesiąc urodzenia (A-T z wyłączeniem nieużywanych liter)",
      "Automatyczne wykrywanie omocodii"
    ],
    important: "Ważne",
    disclaimer: "To narzędzie weryfikuje FORMALNĄ poprawność kodu podatkowego. Nie może potwierdzić, czy CF jest faktycznie przypisany do prawdziwej osoby.",
    faq: [
      { q: "Co oznacza nieprawidłowy CIN?", a: "CIN jest obliczany na pierwszych 15 znakach. Jeśli nie pasuje, kod zawiera błąd transkrypcji." },
      { q: "Czy weryfikuje kody omocodia?", a: "Tak! System automatycznie rozpoznaje kody z omocodią i weryfikuje je poprawnie." },
      { q: "Czy mogę zweryfikować, czy CF naprawdę istnieje?", a: "Nie, tylko Urząd Skarbowy może potwierdzić przypisanie. Weryfikujemy tylko formalną poprawność." }
    ]
  }
};

const VerifyPage = () => {
  const { locale } = useI18n();
  const content = VERIFY_CONTENT[locale] || VERIFY_CONTENT.en;

  return (
    <>
      <SEOHead routeKey="verify" />
      <section className="py-12 bg-gray-50" id="main-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{content.title}</h1>
            <p className="text-lg text-gray-600">{content.subtitle}</p>
          </div>

          {/* Intro */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
            <p className="text-gray-600 leading-relaxed">{content.intro}</p>
          </div>

          {/* Form */}
          <div className="mb-8">
            <VerifyForm />
          </div>

          {/* What We Check */}
          <div className="bg-emerald-50 rounded-2xl p-6 mb-8 border border-emerald-200">
            <h2 className="text-xl font-bold text-emerald-900 mb-4">{content.whatWeCheck}</h2>
            <ul className="space-y-2">
              {content.checks.map((check, idx) => (
                <li key={idx} className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  {check}
                </li>
              ))}
            </ul>
          </div>

          {/* Important Disclaimer */}
          <div className="bg-amber-50 rounded-2xl p-6 mb-8 border border-amber-200">
            <h2 className="text-xl font-bold text-amber-900 mb-2">⚠️ {content.important}</h2>
            <p className="text-amber-800">{content.disclaimer}</p>
          </div>

          {/* FAQ */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">FAQ</h2>
            <div className="space-y-4">
              {content.faq.map((item, idx) => (
                <div key={idx} className="border-b border-gray-200 pb-4 last:border-0">
                  <h3 className="font-semibold text-gray-900 mb-2">{item.q}</h3>
                  <p className="text-gray-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// Health Card Page - Enhanced with educational content
const HEALTH_CARD_CONTENT = {
  it: {
    title: "Genera Tessera Sanitaria",
    subtitle: "Crea un'anteprima PDF della Tessera Sanitaria italiana con codice a barre",
    intro: "Questo strumento genera un'anteprima visiva della Tessera Sanitaria italiana (TS) partendo dal tuo Codice Fiscale. Include il codice a barre e tutti i campi standard. ATTENZIONE: è solo a scopo dimostrativo, NON è un documento ufficiale valido.",
    whatIs: "Cos'è la Tessera Sanitaria",
    whatIsDesc: "La Tessera Sanitaria (TS) è il documento personale che attesta l'iscrizione al Servizio Sanitario Nazionale (SSN). Contiene il Codice Fiscale, i dati anagrafici e permette l'accesso alle prestazioni sanitarie in Italia e in Europa (lato TEAM).",
    features: "Cosa Include l'Anteprima",
    featuresList: [
      "Codice Fiscale completo con codice a barre",
      "Nome e cognome del titolare",
      "Data di nascita e sesso",
      "Layout identico alla tessera ufficiale",
      "Esportazione in formato PDF stampabile"
    ],
    warning: "Avvertenza Legale",
    warningText: "Il documento generato è ESCLUSIVAMENTE a scopo dimostrativo ed educativo. NON può essere utilizzato per accedere a servizi sanitari, NON sostituisce la tessera ufficiale emessa dall'Agenzia delle Entrate. L'uso improprio può costituire reato.",
    faq: [
      { q: "Posso usarla in farmacia?", a: "NO. Questa è solo un'anteprima dimostrativa. Per la tessera ufficiale, devi richiederla all'ASL o all'Agenzia delle Entrate." },
      { q: "Come ottengo la tessera vera?", a: "La Tessera Sanitaria viene inviata automaticamente dopo l'iscrizione all'anagrafe. Se smarrita, richiedi il duplicato all'ASL o tramite il portale dell'Agenzia delle Entrate." },
      { q: "La tessera include il lato TEAM?", a: "L'anteprima mostra solo il fronte italiano. Il retro TEAM (Tessera Europea Assicurazione Malattia) è presente solo sulla tessera ufficiale." }
    ]
  },
  en: {
    title: "Generate Health Card",
    subtitle: "Create a PDF preview of the Italian Health Card with barcode",
    intro: "This tool generates a visual preview of the Italian Health Card (Tessera Sanitaria) from your Tax Code. It includes the barcode and all standard fields. WARNING: this is for demonstration purposes only, NOT an official valid document.",
    whatIs: "What is the Health Card",
    whatIsDesc: "The Health Card (Tessera Sanitaria) is the personal document that certifies enrollment in the National Health Service (SSN). It contains the Tax Code, personal data and allows access to healthcare services in Italy and Europe (EHIC side).",
    features: "What the Preview Includes",
    featuresList: [
      "Complete Tax Code with barcode",
      "Holder's first and last name",
      "Date of birth and gender",
      "Layout identical to official card",
      "Export to printable PDF format"
    ],
    warning: "Legal Warning",
    warningText: "The generated document is EXCLUSIVELY for demonstration and educational purposes. It CANNOT be used to access healthcare services, it does NOT replace the official card issued by the Revenue Agency. Improper use may constitute a crime.",
    faq: [
      { q: "Can I use it at the pharmacy?", a: "NO. This is only a demonstration preview. For the official card, you must request it from ASL or the Revenue Agency." },
      { q: "How do I get the real card?", a: "The Health Card is automatically sent after registering at the registry office. If lost, request a duplicate from ASL or through the Revenue Agency portal." },
      { q: "Does the card include the EHIC side?", a: "The preview only shows the Italian front. The EHIC back (European Health Insurance Card) is only present on the official card." }
    ]
  },
  de: {
    title: "Gesundheitskarte Generieren",
    subtitle: "Erstellen Sie eine PDF-Vorschau der italienischen Gesundheitskarte mit Barcode",
    intro: "Dieses Tool generiert eine visuelle Vorschau der italienischen Gesundheitskarte (Tessera Sanitaria) aus Ihrem Steuerkennzeichen. Es enthält den Barcode und alle Standardfelder. WARNUNG: nur zu Demonstrationszwecken, KEIN offizielles gültiges Dokument.",
    whatIs: "Was ist die Gesundheitskarte",
    whatIsDesc: "Die Gesundheitskarte (Tessera Sanitaria) ist das persönliche Dokument, das die Einschreibung im Nationalen Gesundheitsdienst (SSN) bescheinigt. Sie enthält das Steuerkennzeichen, persönliche Daten und ermöglicht den Zugang zu Gesundheitsleistungen.",
    features: "Was die Vorschau enthält",
    featuresList: [
      "Vollständiges Steuerkennzeichen mit Barcode",
      "Vor- und Nachname des Inhabers",
      "Geburtsdatum und Geschlecht",
      "Layout identisch mit der offiziellen Karte",
      "Export in druckbares PDF-Format"
    ],
    warning: "Rechtlicher Hinweis",
    warningText: "Das generierte Dokument dient AUSSCHLIESSLICH zu Demonstrations- und Bildungszwecken. Es kann NICHT für den Zugang zu Gesundheitsleistungen verwendet werden.",
    faq: [
      { q: "Kann ich sie in der Apotheke verwenden?", a: "NEIN. Dies ist nur eine Demonstrationsvorschau. Für die offizielle Karte müssen Sie sich an ASL wenden." },
      { q: "Wie bekomme ich die echte Karte?", a: "Die Gesundheitskarte wird automatisch nach der Registrierung gesendet. Bei Verlust fordern Sie ein Duplikat bei ASL an." },
      { q: "Enthält die Karte die EHIC-Seite?", a: "Die Vorschau zeigt nur die italienische Vorderseite. Die EHIC-Rückseite ist nur auf der offiziellen Karte vorhanden." }
    ]
  },
  fr: {
    title: "Générer la Carte Santé",
    subtitle: "Créez un aperçu PDF de la Carte Santé italienne avec code-barres",
    intro: "Cet outil génère un aperçu visuel de la Carte Santé italienne (Tessera Sanitaria) à partir de votre Code Fiscal. Il comprend le code-barres et tous les champs standard. ATTENTION: à des fins de démonstration uniquement, PAS un document officiel valide.",
    whatIs: "Qu'est-ce que la Carte Santé",
    whatIsDesc: "La Carte Santé (Tessera Sanitaria) est le document personnel qui certifie l'inscription au Service National de Santé (SSN). Elle contient le Code Fiscal, les données personnelles et permet l'accès aux soins de santé.",
    features: "Ce que l'aperçu inclut",
    featuresList: [
      "Code Fiscal complet avec code-barres",
      "Nom et prénom du titulaire",
      "Date de naissance et sexe",
      "Mise en page identique à la carte officielle",
      "Export au format PDF imprimable"
    ],
    warning: "Avertissement Légal",
    warningText: "Le document généré est EXCLUSIVEMENT à des fins de démonstration et d'éducation. Il NE PEUT PAS être utilisé pour accéder aux services de santé.",
    faq: [
      { q: "Puis-je l'utiliser à la pharmacie?", a: "NON. Ceci n'est qu'un aperçu de démonstration. Pour la carte officielle, vous devez la demander à l'ASL." },
      { q: "Comment obtenir la vraie carte?", a: "La Carte Santé est envoyée automatiquement après l'inscription. En cas de perte, demandez un duplicata à l'ASL." },
      { q: "La carte inclut-elle le côté CEAM?", a: "L'aperçu ne montre que le recto italien. Le verso CEAM n'est présent que sur la carte officielle." }
    ]
  },
  es: {
    title: "Generar Tarjeta Sanitaria",
    subtitle: "Crea una vista previa PDF de la Tarjeta Sanitaria italiana con código de barras",
    intro: "Esta herramienta genera una vista previa visual de la Tarjeta Sanitaria italiana (Tessera Sanitaria) a partir de tu Código Fiscal. Incluye el código de barras y todos los campos estándar. ADVERTENCIA: es solo para fines demostrativos, NO es un documento oficial válido.",
    whatIs: "Qué es la Tarjeta Sanitaria",
    whatIsDesc: "La Tarjeta Sanitaria (Tessera Sanitaria) es el documento personal que certifica la inscripción en el Servicio Nacional de Salud (SSN). Contiene el Código Fiscal, los datos personales y permite el acceso a los servicios sanitarios.",
    features: "Qué incluye la vista previa",
    featuresList: [
      "Código Fiscal completo con código de barras",
      "Nombre y apellido del titular",
      "Fecha de nacimiento y sexo",
      "Diseño idéntico a la tarjeta oficial",
      "Exportación a formato PDF imprimible"
    ],
    warning: "Advertencia Legal",
    warningText: "El documento generado es EXCLUSIVAMENTE para fines demostrativos y educativos. NO puede usarse para acceder a servicios sanitarios.",
    faq: [
      { q: "¿Puedo usarla en la farmacia?", a: "NO. Esta es solo una vista previa demostrativa. Para la tarjeta oficial, debes solicitarla en la ASL." },
      { q: "¿Cómo obtengo la tarjeta real?", a: "La Tarjeta Sanitaria se envía automáticamente después del registro. Si se pierde, solicita un duplicado en la ASL." },
      { q: "¿La tarjeta incluye el lado TSE?", a: "La vista previa solo muestra el frente italiano. El reverso TSE solo está presente en la tarjeta oficial." }
    ]
  },
  pt: {
    title: "Gerar Cartão de Saúde",
    subtitle: "Crie uma pré-visualização PDF do Cartão de Saúde italiano com código de barras",
    intro: "Esta ferramenta gera uma pré-visualização visual do Cartão de Saúde italiano (Tessera Sanitaria) a partir do seu Código Fiscal. Inclui o código de barras e todos os campos padrão. AVISO: é apenas para fins demonstrativos, NÃO é um documento oficial válido.",
    whatIs: "O que é o Cartão de Saúde",
    whatIsDesc: "O Cartão de Saúde (Tessera Sanitaria) é o documento pessoal que certifica a inscrição no Serviço Nacional de Saúde (SSN). Contém o Código Fiscal, dados pessoais e permite o acesso aos serviços de saúde.",
    features: "O que a pré-visualização inclui",
    featuresList: [
      "Código Fiscal completo com código de barras",
      "Nome e sobrenome do titular",
      "Data de nascimento e sexo",
      "Layout idêntico ao cartão oficial",
      "Exportação para formato PDF imprimível"
    ],
    warning: "Aviso Legal",
    warningText: "O documento gerado é EXCLUSIVAMENTE para fins demonstrativos e educacionais. NÃO pode ser usado para acessar serviços de saúde.",
    faq: [
      { q: "Posso usá-lo na farmácia?", a: "NÃO. Esta é apenas uma pré-visualização demonstrativa. Para o cartão oficial, você deve solicitá-lo na ASL." },
      { q: "Como obtenho o cartão real?", a: "O Cartão de Saúde é enviado automaticamente após o registro. Se perdido, solicite um duplicado na ASL." },
      { q: "O cartão inclui o lado CESD?", a: "A pré-visualização mostra apenas a frente italiana. O verso CESD só está presente no cartão oficial." }
    ]
  },
  ro: {
    title: "Generează Cardul de Sănătate",
    subtitle: "Creați o previzualizare PDF a Cardului de Sănătate italian cu cod de bare",
    intro: "Acest instrument generează o previzualizare vizuală a Cardului de Sănătate italian (Tessera Sanitaria) din Codul Fiscal. Include codul de bare și toate câmpurile standard. AVERTISMENT: este doar în scop demonstrativ, NU este un document oficial valid.",
    whatIs: "Ce este Cardul de Sănătate",
    whatIsDesc: "Cardul de Sănătate (Tessera Sanitaria) este documentul personal care certifică înscrierea în Serviciul Național de Sănătate (SSN). Conține Codul Fiscal, datele personale și permite accesul la serviciile de sănătate.",
    features: "Ce include previzualizarea",
    featuresList: [
      "Codul Fiscal complet cu cod de bare",
      "Numele și prenumele titularului",
      "Data nașterii și sexul",
      "Aspect identic cu cardul oficial",
      "Export în format PDF imprimabil"
    ],
    warning: "Avertisment Legal",
    warningText: "Documentul generat este EXCLUSIV în scop demonstrativ și educativ. NU poate fi folosit pentru a accesa servicii de sănătate.",
    faq: [
      { q: "Pot să-l folosesc la farmacie?", a: "NU. Aceasta este doar o previzualizare demonstrativă. Pentru cardul oficial, trebuie să-l solicitați de la ASL." },
      { q: "Cum obțin cardul real?", a: "Cardul de Sănătate este trimis automat după înregistrare. Dacă este pierdut, solicitați un duplicat de la ASL." },
      { q: "Cardul include partea CEASS?", a: "Previzualizarea arată doar partea italiană. Partea CEASS este prezentă doar pe cardul oficial." }
    ]
  },
  pl: {
    title: "Generuj Kartę Zdrowia",
    subtitle: "Utwórz podgląd PDF włoskiej Karty Zdrowia z kodem kreskowym",
    intro: "To narzędzie generuje wizualny podgląd włoskiej Karty Zdrowia (Tessera Sanitaria) z Twojego Kodu Podatkowego. Zawiera kod kreskowy i wszystkie standardowe pola. OSTRZEŻENIE: służy wyłącznie do celów demonstracyjnych, NIE jest oficjalnym ważnym dokumentem.",
    whatIs: "Czym jest Karta Zdrowia",
    whatIsDesc: "Karta Zdrowia (Tessera Sanitaria) to osobisty dokument poświadczający rejestrację w Narodowej Służbie Zdrowia (SSN). Zawiera Kod Podatkowy, dane osobowe i umożliwia dostęp do usług zdrowotnych.",
    features: "Co zawiera podgląd",
    featuresList: [
      "Pełny Kod Podatkowy z kodem kreskowym",
      "Imię i nazwisko posiadacza",
      "Data urodzenia i płeć",
      "Układ identyczny z oficjalną kartą",
      "Eksport do formatu PDF do druku"
    ],
    warning: "Ostrzeżenie Prawne",
    warningText: "Wygenerowany dokument służy WYŁĄCZNIE do celów demonstracyjnych i edukacyjnych. NIE może być używany do uzyskania dostępu do usług zdrowotnych.",
    faq: [
      { q: "Czy mogę jej użyć w aptece?", a: "NIE. To tylko podgląd demonstracyjny. Oficjalną kartę musisz uzyskać w ASL." },
      { q: "Jak uzyskać prawdziwą kartę?", a: "Karta Zdrowia jest wysyłana automatycznie po rejestracji. W przypadku utraty poproś o duplikat w ASL." },
      { q: "Czy karta zawiera stronę EKUZ?", a: "Podgląd pokazuje tylko włoską przednią stronę. Tył EKUZ jest obecny tylko na oficjalnej karcie." }
    ]
  }
};

const HealthCardPage = () => {
  const { locale } = useI18n();
  const content = HEALTH_CARD_CONTENT[locale] || HEALTH_CARD_CONTENT.en;

  return (
    <>
      <SEOHead routeKey="healthCard" />
      <section className="py-12 bg-gray-50" id="main-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{content.title}</h1>
            <p className="text-lg text-gray-600">{content.subtitle}</p>
          </div>

          {/* Intro */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
            <p className="text-gray-600 leading-relaxed">{content.intro}</p>
          </div>

          {/* Warning */}
          <div className="bg-red-50 rounded-2xl p-6 mb-8 border border-red-200">
            <h2 className="text-xl font-bold text-red-900 mb-2">⚠️ {content.warning}</h2>
            <p className="text-red-800">{content.warningText}</p>
          </div>

          {/* Form */}
          <div className="mb-8">
            <Suspense fallback={<LazyLoadingFallback />}>
              <HealthCardGenerator />
            </Suspense>
          </div>

          {/* What Is */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{content.whatIs}</h2>
            <p className="text-gray-600">{content.whatIsDesc}</p>
          </div>

          {/* Features */}
          <div className="bg-emerald-50 rounded-2xl p-6 mb-8 border border-emerald-200">
            <h2 className="text-xl font-bold text-emerald-900 mb-4">{content.features}</h2>
            <ul className="space-y-2">
              {content.featuresList.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* FAQ */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">FAQ</h2>
            <div className="space-y-4">
              {content.faq.map((item, idx) => (
                <div key={idx} className="border-b border-gray-200 pb-4 last:border-0">
                  <h3 className="font-semibold text-gray-900 mb-2">{item.q}</h3>
                  <p className="text-gray-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// Municipalities Page - Enhanced with educational content
const MUNICIPALITIES_CONTENT = {
  it: {
    title: "Codici Catastali Comuni Italiani",
    subtitle: "Cerca il codice catastale di tutti i 7.904 comuni italiani per il Codice Fiscale",
    intro: "Il codice catastale (o codice Belfiore) è un codice alfanumerico di 4 caratteri assegnato ad ogni comune italiano dall'Agenzia del Territorio. È fondamentale per il calcolo del Codice Fiscale, occupando le posizioni 12-15 del CF.",
    database: "Il Nostro Database",
    databaseStats: [
      "7.904 comuni italiani attivi",
      "Comuni soppressi e accorpati",
      "Stati esteri con codice Z",
      "Aggiornato al 2026"
    ],
    howToUse: "Come Usare la Ricerca",
    howToUseSteps: [
      "Digita almeno 2 caratteri del nome del comune",
      "Seleziona dalla lista dei risultati",
      "Copia il codice catastale per il tuo CF"
    ],
    structure: "Struttura del Codice Catastale",
    structureDesc: "Il codice è composto da 1 lettera + 3 cifre. Esempi: H501 (Roma), F205 (Milano), F839 (Napoli). I codici che iniziano con Z identificano stati esteri (es. Z112 = Germania).",
    faq: [
      { q: "Cosa fare se il comune è stato soppresso?", a: "Usa il codice del comune esistente al momento della nascita. I comuni soppressi mantengono validità per i CF già emessi." },
      { q: "Come trovo il codice per chi è nato all'estero?", a: "Cerca il nome dello stato. I codici esteri iniziano con Z: Z112 (Germania), Z110 (Francia), Z129 (Romania), etc." },
      { q: "Perché alcuni comuni hanno lo stesso codice?", a: "Non è possibile. Ogni comune ha un codice univoco. Se trovi duplicati, verifica l'ortografia corretta." }
    ]
  },
  en: {
    title: "Italian Municipality Cadastral Codes",
    subtitle: "Search cadastral codes for all 7,904 Italian municipalities for Tax Code calculation",
    intro: "The cadastral code (or Belfiore code) is a 4-character alphanumeric code assigned to each Italian municipality by the Land Agency. It's essential for Tax Code calculation, occupying positions 12-15 of the CF.",
    database: "Our Database",
    databaseStats: [
      "7,904 active Italian municipalities",
      "Suppressed and merged municipalities",
      "Foreign countries with Z code",
      "Updated to 2026"
    ],
    howToUse: "How to Use Search",
    howToUseSteps: [
      "Type at least 2 characters of municipality name",
      "Select from the results list",
      "Copy the cadastral code for your CF"
    ],
    structure: "Cadastral Code Structure",
    structureDesc: "The code consists of 1 letter + 3 digits. Examples: H501 (Rome), F205 (Milan), F839 (Naples). Codes starting with Z identify foreign countries (e.g., Z112 = Germany).",
    faq: [
      { q: "What if the municipality was suppressed?", a: "Use the code of the municipality existing at birth. Suppressed municipalities remain valid for already issued CFs." },
      { q: "How do I find the code for someone born abroad?", a: "Search the country name. Foreign codes start with Z: Z112 (Germany), Z110 (France), Z129 (Romania), etc." },
      { q: "Why do some municipalities have the same code?", a: "That's not possible. Each municipality has a unique code. If you find duplicates, verify the correct spelling." }
    ]
  },
  de: {
    title: "Katastercodes italienischer Gemeinden",
    subtitle: "Suchen Sie Katastercodes für alle 7.904 italienischen Gemeinden zur Steuerkennzeichen-Berechnung",
    intro: "Der Katastercode (oder Belfiore-Code) ist ein 4-stelliger alphanumerischer Code, der jeder italienischen Gemeinde zugewiesen wird. Er ist wesentlich für die Berechnung des Steuerkennzeichens, Positionen 12-15.",
    database: "Unsere Datenbank",
    databaseStats: [
      "7.904 aktive italienische Gemeinden",
      "Aufgelöste und zusammengelegte Gemeinden",
      "Ausländische Länder mit Z-Code",
      "Aktualisiert auf 2026"
    ],
    howToUse: "So verwenden Sie die Suche",
    howToUseSteps: [
      "Geben Sie mindestens 2 Zeichen des Gemeindenamens ein",
      "Wählen Sie aus der Ergebnisliste",
      "Kopieren Sie den Katastercode für Ihr CF"
    ],
    structure: "Struktur des Katastercodes",
    structureDesc: "Der Code besteht aus 1 Buchstabe + 3 Ziffern. Beispiele: H501 (Rom), F205 (Mailand). Codes mit Z kennzeichnen ausländische Länder.",
    faq: [
      { q: "Was wenn die Gemeinde aufgelöst wurde?", a: "Verwenden Sie den Code der Gemeinde zum Zeitpunkt der Geburt. Aufgelöste Gemeinden bleiben für bereits ausgestellte CFs gültig." },
      { q: "Wie finde ich den Code für im Ausland Geborene?", a: "Suchen Sie den Ländernamen. Auslandscodes beginnen mit Z: Z112 (Deutschland), Z110 (Frankreich)." },
      { q: "Warum haben einige Gemeinden den gleichen Code?", a: "Das ist nicht möglich. Jede Gemeinde hat einen einzigartigen Code." }
    ]
  },
  fr: {
    title: "Codes Cadastraux des Communes Italiennes",
    subtitle: "Recherchez les codes cadastraux des 7 904 communes italiennes pour le Code Fiscal",
    intro: "Le code cadastral (ou code Belfiore) est un code alphanumérique de 4 caractères attribué à chaque commune italienne. Il est essentiel pour le calcul du Code Fiscal, positions 12-15.",
    database: "Notre Base de Données",
    databaseStats: [
      "7 904 communes italiennes actives",
      "Communes supprimées et fusionnées",
      "Pays étrangers avec code Z",
      "Mis à jour 2026"
    ],
    howToUse: "Comment utiliser la recherche",
    howToUseSteps: [
      "Tapez au moins 2 caractères du nom de la commune",
      "Sélectionnez dans la liste des résultats",
      "Copiez le code cadastral pour votre CF"
    ],
    structure: "Structure du Code Cadastral",
    structureDesc: "Le code se compose de 1 lettre + 3 chiffres. Exemples: H501 (Rome), F205 (Milan). Les codes commençant par Z identifient les pays étrangers.",
    faq: [
      { q: "Que faire si la commune a été supprimée?", a: "Utilisez le code de la commune existante à la naissance. Les communes supprimées restent valides pour les CF déjà émis." },
      { q: "Comment trouver le code pour quelqu'un né à l'étranger?", a: "Recherchez le nom du pays. Les codes étrangers commencent par Z: Z112 (Allemagne), Z110 (France)." },
      { q: "Pourquoi certaines communes ont-elles le même code?", a: "Ce n'est pas possible. Chaque commune a un code unique." }
    ]
  },
  es: {
    title: "Códigos Catastrales de Municipios Italianos",
    subtitle: "Busca códigos catastrales de los 7.904 municipios italianos para el Código Fiscal",
    intro: "El código catastral (o código Belfiore) es un código alfanumérico de 4 caracteres asignado a cada municipio italiano. Es esencial para el cálculo del Código Fiscal, posiciones 12-15.",
    database: "Nuestra Base de Datos",
    databaseStats: [
      "7.904 municipios italianos activos",
      "Municipios suprimidos y fusionados",
      "Países extranjeros con código Z",
      "Actualizado a 2026"
    ],
    howToUse: "Cómo usar la búsqueda",
    howToUseSteps: [
      "Escribe al menos 2 caracteres del nombre del municipio",
      "Selecciona de la lista de resultados",
      "Copia el código catastral para tu CF"
    ],
    structure: "Estructura del Código Catastral",
    structureDesc: "El código consta de 1 letra + 3 dígitos. Ejemplos: H501 (Roma), F205 (Milán). Los códigos que empiezan con Z identifican países extranjeros.",
    faq: [
      { q: "¿Qué hacer si el municipio fue suprimido?", a: "Usa el código del municipio existente al nacer. Los municipios suprimidos siguen siendo válidos para los CF ya emitidos." },
      { q: "¿Cómo encuentro el código para alguien nacido en el extranjero?", a: "Busca el nombre del país. Los códigos extranjeros empiezan con Z: Z112 (Alemania), Z110 (Francia)." },
      { q: "¿Por qué algunos municipios tienen el mismo código?", a: "Eso no es posible. Cada municipio tiene un código único." }
    ]
  },
  pt: {
    title: "Códigos Cadastrais dos Municípios Italianos",
    subtitle: "Pesquise códigos cadastrais de todos os 7.904 municípios italianos para o Código Fiscal",
    intro: "O código cadastral (ou código Belfiore) é um código alfanumérico de 4 caracteres atribuído a cada município italiano. É essencial para o cálculo do Código Fiscal, posições 12-15.",
    database: "Nossa Base de Dados",
    databaseStats: [
      "7.904 municípios italianos ativos",
      "Municípios suprimidos e fundidos",
      "Países estrangeiros com código Z",
      "Atualizado para 2026"
    ],
    howToUse: "Como usar a pesquisa",
    howToUseSteps: [
      "Digite pelo menos 2 caracteres do nome do município",
      "Selecione da lista de resultados",
      "Copie o código cadastral para seu CF"
    ],
    structure: "Estrutura do Código Cadastral",
    structureDesc: "O código consiste em 1 letra + 3 dígitos. Exemplos: H501 (Roma), F205 (Milão). Códigos que começam com Z identificam países estrangeiros.",
    faq: [
      { q: "O que fazer se o município foi suprimido?", a: "Use o código do município existente no nascimento. Municípios suprimidos permanecem válidos para CFs já emitidos." },
      { q: "Como encontro o código para alguém nascido no exterior?", a: "Pesquise o nome do país. Códigos estrangeiros começam com Z: Z112 (Alemanha), Z110 (França)." },
      { q: "Por que alguns municípios têm o mesmo código?", a: "Isso não é possível. Cada município tem um código único." }
    ]
  },
  ro: {
    title: "Coduri Cadastrale ale Municipiilor Italiene",
    subtitle: "Căutați coduri cadastrale pentru toate cele 7.904 municipii italiene pentru Codul Fiscal",
    intro: "Codul cadastral (sau codul Belfiore) este un cod alfanumeric de 4 caractere atribuit fiecărui municipiu italian. Este esențial pentru calculul Codului Fiscal, pozițiile 12-15.",
    database: "Baza Noastră de Date",
    databaseStats: [
      "7.904 municipii italiene active",
      "Municipii desființate și fuzionate",
      "Țări străine cu cod Z",
      "Actualizat la 2026"
    ],
    howToUse: "Cum să folosiți căutarea",
    howToUseSteps: [
      "Tastați cel puțin 2 caractere din numele municipiului",
      "Selectați din lista de rezultate",
      "Copiați codul cadastral pentru CF-ul dvs."
    ],
    structure: "Structura Codului Cadastral",
    structureDesc: "Codul constă din 1 literă + 3 cifre. Exemple: H501 (Roma), F205 (Milano). Codurile care încep cu Z identifică țări străine.",
    faq: [
      { q: "Ce să fac dacă municipiul a fost desființat?", a: "Folosiți codul municipiului existent la naștere. Municipiile desființate rămân valide pentru CF-urile deja emise." },
      { q: "Cum găsesc codul pentru cineva născut în străinătate?", a: "Căutați numele țării. Codurile străine încep cu Z: Z112 (Germania), Z110 (Franța)." },
      { q: "De ce unele municipii au același cod?", a: "Nu este posibil. Fiecare municipiu are un cod unic." }
    ]
  },
  pl: {
    title: "Kody Katastralne Włoskich Gmin",
    subtitle: "Wyszukaj kody katastralne dla wszystkich 7904 włoskich gmin do Kodu Podatkowego",
    intro: "Kod katastralny (lub kod Belfiore) to 4-znakowy kod alfanumeryczny przypisany każdej włoskiej gminie. Jest niezbędny do obliczenia Kodu Podatkowego, pozycje 12-15.",
    database: "Nasza Baza Danych",
    databaseStats: [
      "7904 aktywnych włoskich gmin",
      "Zlikwidowane i połączone gminy",
      "Kraje zagraniczne z kodem Z",
      "Zaktualizowane do 2026"
    ],
    howToUse: "Jak korzystać z wyszukiwania",
    howToUseSteps: [
      "Wpisz co najmniej 2 znaki nazwy gminy",
      "Wybierz z listy wyników",
      "Skopiuj kod katastralny dla swojego CF"
    ],
    structure: "Struktura Kodu Katastralnego",
    structureDesc: "Kod składa się z 1 litery + 3 cyfr. Przykłady: H501 (Rzym), F205 (Mediolan). Kody zaczynające się od Z identyfikują kraje zagraniczne.",
    faq: [
      { q: "Co jeśli gmina została zlikwidowana?", a: "Użyj kodu gminy istniejącej w momencie urodzenia. Zlikwidowane gminy pozostają ważne dla już wydanych CF." },
      { q: "Jak znaleźć kod dla kogoś urodzonego za granicą?", a: "Wyszukaj nazwę kraju. Kody zagraniczne zaczynają się od Z: Z112 (Niemcy), Z110 (Francja)." },
      { q: "Dlaczego niektóre gminy mają ten sam kod?", a: "To niemożliwe. Każda gmina ma unikalny kod." }
    ]
  }
};

const MunicipalitiesPage = () => {
  const { locale } = useI18n();
  const content = MUNICIPALITIES_CONTENT[locale] || MUNICIPALITIES_CONTENT.en;

  return (
    <>
      <SEOHead routeKey="municipalities" />
      <section className="py-12 bg-gray-50" id="main-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{content.title}</h1>
            <p className="text-lg text-gray-600">{content.subtitle}</p>
          </div>

          {/* Intro */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
            <p className="text-gray-600 leading-relaxed">{content.intro}</p>
          </div>

          {/* Search Form */}
          <div className="mb-8">
            <MunicipalitySearch />
          </div>

          {/* Database Stats */}
          <div className="bg-emerald-50 rounded-2xl p-6 mb-8 border border-emerald-200">
            <h2 className="text-xl font-bold text-emerald-900 mb-4">{content.database}</h2>
            <ul className="space-y-2">
              {content.databaseStats.map((stat, idx) => (
                <li key={idx} className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  {stat}
                </li>
              ))}
            </ul>
          </div>

          {/* How to Use */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{content.howToUse}</h2>
            <ol className="space-y-3">
              {content.howToUseSteps.map((step, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold">{idx + 1}</span>
                  <span className="text-gray-600">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Structure */}
          <div className="bg-blue-50 rounded-2xl p-6 mb-8 border border-blue-200">
            <h2 className="text-xl font-bold text-blue-900 mb-4">{content.structure}</h2>
            <p className="text-blue-800">{content.structureDesc}</p>
          </div>

          {/* FAQ */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">FAQ</h2>
            <div className="space-y-4">
              {content.faq.map((item, idx) => (
                <div key={idx} className="border-b border-gray-200 pb-4 last:border-0">
                  <h3 className="font-semibold text-gray-900 mb-2">{item.q}</h3>
                  <p className="text-gray-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// How It Works Page
const HowItWorksPage = () => {
  const { t } = useI18n();
  return (
    <>
      <SEOHead routeKey="howItWorks" />
      <section className="py-12 bg-gray-50 min-h-[70vh]" id="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <HowItWorks />
        </div>
      </section>
    </>
  );
};

// Legal Pages (Privacy, Cookie, Terms, Accessibility)
const LegalPage = ({ type }) => {
  const { t, locale } = useI18n();
  
  // Get privacy content based on locale
  const getPrivacyContent = () => {
    const privacyTexts = {
      it: (
        <div className="prose max-w-none">
          <h1>Privacy Policy</h1>
          <p><strong>Ultimo aggiornamento:</strong> Dicembre 2025</p>
          
          <h2>Dati che NON raccogliamo</h2>
          <p>CodiceFiscale.expert è progettato con la privacy come priorità assoluta:</p>
          <ul>
            <li>❌ NON salviamo i dati anagrafici inseriti (nome, cognome, data nascita)</li>
            <li>❌ NON salviamo i Codici Fiscali calcolati</li>
            <li>❌ NON creiamo profili utente</li>
            <li>❌ NON vendiamo dati a terzi</li>
          </ul>
          <p><strong>Tutti i calcoli avvengono nel tuo browser. I tuoi dati non vengono mai inviati ai nostri server.</strong></p>
          
          <h2>Pubblicità (Google AdSense)</h2>
          <p>Utilizziamo Google AdSense per mostrare annunci pubblicitari sul nostro sito. Google e i suoi partner pubblicitari utilizzano cookie per pubblicare annunci basati sulle visite precedenti degli utenti al nostro sito o ad altri siti web.</p>
          <p>L'utilizzo dei cookie da parte di Google consente a Google e ai suoi partner di pubblicare annunci agli utenti in base alla loro visita sui nostri siti e/o su altri siti Internet.</p>
          <p>Puoi scegliere di disattivare la pubblicità personalizzata visitando le <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Impostazioni annunci di Google</a>.</p>
          <p>In alternativa, puoi disattivare l'utilizzo dei cookie da parte di fornitori terzi per la pubblicità personalizzata visitando: <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">www.aboutads.info/choices</a></p>
          <p>Per ulteriori informazioni su come Google utilizza i dati quando visiti il nostro sito, consulta: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/partner-sites</a></p>
          
          <h2>Cookie</h2>
          <p>Utilizziamo solo cookie tecnici essenziali e, previo consenso, cookie analitici e pubblicitari. Vedi la nostra <a href="/cookie">Cookie Policy</a> per dettagli completi.</p>
          
          <h2>I tuoi diritti (GDPR Art. 15-22)</h2>
          <p>Hai il diritto di accesso, rettifica, cancellazione, portabilità, opposizione e revoca del consenso.</p>
          
          <h2>Contatti</h2>
          <p>Per domande sulla privacy: privacy@codicefiscale.expert</p>
        </div>
      ),
      en: (
        <div className="prose max-w-none">
          <h1>Privacy Policy</h1>
          <p><strong>Last updated:</strong> December 2025</p>
          
          <h2>Data we DON'T collect</h2>
          <p>CodiceFiscale.expert is designed with privacy as a top priority:</p>
          <ul>
            <li>❌ We DO NOT save personal data entered (name, surname, birth date)</li>
            <li>❌ We DO NOT save calculated Tax Codes</li>
            <li>❌ We DO NOT create user profiles</li>
            <li>❌ We DO NOT sell data to third parties</li>
          </ul>
          <p><strong>All calculations happen in your browser. Your data is never sent to our servers.</strong></p>
          
          <h2>Advertising (Google AdSense)</h2>
          <p>We use Google AdSense to display advertisements on our website. Google and its advertising partners use cookies to serve ads based on users' prior visits to our site or other websites.</p>
          <p>Google's use of cookies enables Google and its partners to serve ads to users based on their visit to our sites and/or other sites on the Internet.</p>
          <p>You can opt out of personalized advertising by visiting <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>.</p>
          <p>Alternatively, you can opt out of third-party vendor's use of cookies for personalized advertising by visiting: <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">www.aboutads.info/choices</a></p>
          <p>For more information about how Google uses data when you visit our site, please see: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/partner-sites</a></p>
          
          <h2>Cookies</h2>
          <p>We only use essential technical cookies and, with consent, analytics and advertising cookies. See our <a href="/en/cookie">Cookie Policy</a> for details.</p>
          
          <h2>Your rights (GDPR Art. 15-22)</h2>
          <p>You have the right to access, rectification, erasure, portability, objection and withdrawal of consent.</p>
          
          <h2>Contact</h2>
          <p>For privacy questions: privacy@codicefiscale.expert</p>
        </div>
      ),
      de: (
        <div className="prose max-w-none">
          <h1>Datenschutzerklärung</h1>
          <p><strong>Letzte Aktualisierung:</strong> Dezember 2025</p>
          
          <h2>Daten, die wir NICHT erfassen</h2>
          <p>CodiceFiscale.expert wurde mit Datenschutz als oberster Priorität entwickelt:</p>
          <ul>
            <li>❌ Wir speichern KEINE eingegebenen persönlichen Daten (Name, Vorname, Geburtsdatum)</li>
            <li>❌ Wir speichern KEINE berechneten Steuernummern</li>
            <li>❌ Wir erstellen KEINE Benutzerprofile</li>
            <li>❌ Wir verkaufen KEINE Daten an Dritte</li>
          </ul>
          <p><strong>Alle Berechnungen erfolgen in Ihrem Browser. Ihre Daten werden niemals an unsere Server gesendet.</strong></p>
          
          <h2>Werbung (Google AdSense)</h2>
          <p>Wir verwenden Google AdSense, um Werbung auf unserer Website anzuzeigen. Google und seine Werbepartner verwenden Cookies, um Anzeigen basierend auf früheren Besuchen der Nutzer auf unserer Website oder anderen Websites zu schalten.</p>
          <p>Die Verwendung von Cookies durch Google ermöglicht es Google und seinen Partnern, Nutzern Anzeigen basierend auf ihrem Besuch auf unseren Websites und/oder anderen Websites im Internet zu präsentieren.</p>
          <p>Sie können personalisierte Werbung deaktivieren, indem Sie die <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Google Ads-Einstellungen</a> besuchen.</p>
          <p>Alternativ können Sie die Verwendung von Cookies durch Drittanbieter für personalisierte Werbung deaktivieren unter: <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">www.aboutads.info/choices</a></p>
          <p>Weitere Informationen darüber, wie Google Daten verwendet, wenn Sie unsere Website besuchen, finden Sie unter: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/partner-sites</a></p>
          
          <h2>Cookies</h2>
          <p>Wir verwenden nur wesentliche technische Cookies und, mit Zustimmung, Analyse- und Werbe-Cookies. Siehe unsere <a href="/de/cookie">Cookie-Richtlinie</a> für Details.</p>
          
          <h2>Ihre Rechte (DSGVO Art. 15-22)</h2>
          <p>Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Datenübertragbarkeit, Widerspruch und Widerruf der Einwilligung.</p>
          
          <h2>Kontakt</h2>
          <p>Für Datenschutzfragen: privacy@codicefiscale.expert</p>
        </div>
      ),
      fr: (
        <div className="prose max-w-none">
          <h1>Politique de Confidentialité</h1>
          <p><strong>Dernière mise à jour:</strong> Décembre 2025</p>
          
          <h2>Données que nous NE collectons PAS</h2>
          <p>CodiceFiscale.expert est conçu avec la confidentialité comme priorité absolue:</p>
          <ul>
            <li>❌ Nous NE sauvegardons PAS les données personnelles saisies (nom, prénom, date de naissance)</li>
            <li>❌ Nous NE sauvegardons PAS les codes fiscaux calculés</li>
            <li>❌ Nous NE créons PAS de profils utilisateurs</li>
            <li>❌ Nous NE vendons PAS de données à des tiers</li>
          </ul>
          <p><strong>Tous les calculs sont effectués dans votre navigateur. Vos données ne sont jamais envoyées à nos serveurs.</strong></p>
          
          <h2>Publicité (Google AdSense)</h2>
          <p>Nous utilisons Google AdSense pour afficher des publicités sur notre site web. Google et ses partenaires publicitaires utilisent des cookies pour diffuser des annonces basées sur les visites précédentes des utilisateurs sur notre site ou d'autres sites web.</p>
          <p>L'utilisation de cookies par Google permet à Google et à ses partenaires de diffuser des annonces aux utilisateurs en fonction de leur visite sur nos sites et/ou d'autres sites Internet.</p>
          <p>Vous pouvez désactiver la publicité personnalisée en visitant les <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Paramètres des annonces Google</a>.</p>
          <p>Vous pouvez également désactiver l'utilisation des cookies par des fournisseurs tiers pour la publicité personnalisée en visitant: <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">www.aboutads.info/choices</a></p>
          <p>Pour plus d'informations sur la façon dont Google utilise les données lorsque vous visitez notre site, consultez: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/partner-sites</a></p>
          
          <h2>Cookies</h2>
          <p>Nous utilisons uniquement des cookies techniques essentiels et, avec consentement, des cookies analytiques et publicitaires. Voir notre <a href="/fr/cookies">Politique de Cookies</a> pour les détails.</p>
          
          <h2>Vos droits (RGPD Art. 15-22)</h2>
          <p>Vous avez le droit d'accès, de rectification, d'effacement, de portabilité, d'opposition et de retrait du consentement.</p>
          
          <h2>Contact</h2>
          <p>Pour les questions de confidentialité: privacy@codicefiscale.expert</p>
        </div>
      ),
      es: (
        <div className="prose max-w-none">
          <h1>Política de Privacidad</h1>
          <p><strong>Última actualización:</strong> Diciembre 2025</p>
          
          <h2>Datos que NO recopilamos</h2>
          <p>CodiceFiscale.expert está diseñado con la privacidad como prioridad absoluta:</p>
          <ul>
            <li>❌ NO guardamos los datos personales introducidos (nombre, apellido, fecha de nacimiento)</li>
            <li>❌ NO guardamos los códigos fiscales calculados</li>
            <li>❌ NO creamos perfiles de usuario</li>
            <li>❌ NO vendemos datos a terceros</li>
          </ul>
          <p><strong>Todos los cálculos se realizan en tu navegador. Tus datos nunca se envían a nuestros servidores.</strong></p>
          
          <h2>Publicidad (Google AdSense)</h2>
          <p>Utilizamos Google AdSense para mostrar anuncios en nuestro sitio web. Google y sus socios publicitarios utilizan cookies para publicar anuncios basados en las visitas anteriores de los usuarios a nuestro sitio u otros sitios web.</p>
          <p>El uso de cookies por parte de Google permite a Google y sus socios mostrar anuncios a los usuarios en función de su visita a nuestros sitios y/o a otros sitios de Internet.</p>
          <p>Puede optar por desactivar la publicidad personalizada visitando la <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Configuración de anuncios de Google</a>.</p>
          <p>Alternativamente, puede desactivar el uso de cookies de terceros para publicidad personalizada visitando: <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">www.aboutads.info/choices</a></p>
          <p>Para obtener más información sobre cómo Google utiliza los datos cuando visita nuestro sitio, consulte: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/partner-sites</a></p>
          
          <h2>Cookies</h2>
          <p>Solo utilizamos cookies técnicas esenciales y, con consentimiento, cookies analíticas y publicitarias. Consulte nuestra <a href="/es/cookies">Política de Cookies</a> para más detalles.</p>
          
          <h2>Tus derechos (RGPD Art. 15-22)</h2>
          <p>Tienes derecho de acceso, rectificación, supresión, portabilidad, oposición y revocación del consentimiento.</p>
          
          <h2>Contacto</h2>
          <p>Para preguntas sobre privacidad: privacy@codicefiscale.expert</p>
        </div>
      ),
      pt: (
        <div className="prose max-w-none">
          <h1>Política de Privacidade</h1>
          <p><strong>Última atualização:</strong> Dezembro 2025</p>
          
          <h2>Dados que NÃO coletamos</h2>
          <p>CodiceFiscale.expert foi projetado com a privacidade como prioridade absoluta:</p>
          <ul>
            <li>❌ NÃO salvamos os dados pessoais inseridos (nome, sobrenome, data de nascimento)</li>
            <li>❌ NÃO salvamos os códigos fiscais calculados</li>
            <li>❌ NÃO criamos perfis de usuário</li>
            <li>❌ NÃO vendemos dados a terceiros</li>
          </ul>
          <p><strong>Todos os cálculos acontecem no seu navegador. Seus dados nunca são enviados aos nossos servidores.</strong></p>
          
          <h2>Publicidade (Google AdSense)</h2>
          <p>Utilizamos o Google AdSense para exibir anúncios em nosso site. O Google e seus parceiros de publicidade usam cookies para veicular anúncios com base nas visitas anteriores dos usuários ao nosso site ou a outros sites.</p>
          <p>O uso de cookies pelo Google permite que o Google e seus parceiros veiculem anúncios aos usuários com base em suas visitas aos nossos sites e/ou a outros sites na Internet.</p>
          <p>Você pode optar por desativar a publicidade personalizada visitando as <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Configurações de anúncios do Google</a>.</p>
          <p>Alternativamente, você pode desativar o uso de cookies por fornecedores terceiros para publicidade personalizada visitando: <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">www.aboutads.info/choices</a></p>
          <p>Para mais informações sobre como o Google usa os dados quando você visita nosso site, consulte: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/partner-sites</a></p>
          
          <h2>Cookies</h2>
          <p>Usamos apenas cookies técnicos essenciais e, com consentimento, cookies analíticos e publicitários. Veja nossa <a href="/pt/cookies">Política de Cookies</a> para detalhes.</p>
          
          <h2>Seus direitos (RGPD Art. 15-22)</h2>
          <p>Você tem direito de acesso, retificação, exclusão, portabilidade, oposição e revogação do consentimento.</p>
          
          <h2>Contato</h2>
          <p>Para questões de privacidade: privacy@codicefiscale.expert</p>
        </div>
      ),
      ro: (
        <div className="prose max-w-none">
          <h1>Politica de Confidențialitate</h1>
          <p><strong>Ultima actualizare:</strong> Decembrie 2025</p>
          
          <h2>Date pe care NU le colectăm</h2>
          <p>CodiceFiscale.expert este conceput cu confidențialitatea ca prioritate absolută:</p>
          <ul>
            <li>❌ NU salvăm datele personale introduse (nume, prenume, data nașterii)</li>
            <li>❌ NU salvăm codurile fiscale calculate</li>
            <li>❌ NU creăm profiluri de utilizator</li>
            <li>❌ NU vindem date terților</li>
          </ul>
          <p><strong>Toate calculele au loc în browserul tău. Datele tale nu sunt niciodată trimise la serverele noastre.</strong></p>
          
          <h2>Publicitate (Google AdSense)</h2>
          <p>Folosim Google AdSense pentru a afișa reclame pe site-ul nostru. Google și partenerii săi de publicitate folosesc cookie-uri pentru a difuza anunțuri bazate pe vizitele anterioare ale utilizatorilor pe site-ul nostru sau pe alte site-uri web.</p>
          <p>Utilizarea cookie-urilor de către Google permite Google și partenerilor săi să difuzeze anunțuri utilizatorilor pe baza vizitei lor pe site-urile noastre și/sau alte site-uri de pe Internet.</p>
          <p>Puteți alege să dezactivați publicitatea personalizată vizitând <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Setările pentru anunțuri Google</a>.</p>
          <p>Alternativ, puteți dezactiva utilizarea cookie-urilor de către furnizori terți pentru publicitate personalizată vizitând: <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">www.aboutads.info/choices</a></p>
          <p>Pentru mai multe informații despre modul în care Google utilizează datele când vizitați site-ul nostru, consultați: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/partner-sites</a></p>
          
          <h2>Cookie-uri</h2>
          <p>Folosim doar cookie-uri tehnice esențiale și, cu consimțământ, cookie-uri analitice și publicitare. Consultați <a href="/ro/cookies">Politica de Cookie-uri</a> pentru detalii.</p>
          
          <h2>Drepturile tale (GDPR Art. 15-22)</h2>
          <p>Ai dreptul la acces, rectificare, ștergere, portabilitate, opoziție și retragerea consimțământului.</p>
          
          <h2>Contact</h2>
          <p>Pentru întrebări privind confidențialitatea: privacy@codicefiscale.expert</p>
        </div>
      ),
      pl: (
        <div className="prose max-w-none">
          <h1>Polityka Prywatności</h1>
          <p><strong>Ostatnia aktualizacja:</strong> Grudzień 2025</p>
          
          <h2>Dane, których NIE zbieramy</h2>
          <p>CodiceFiscale.expert został zaprojektowany z prywatnością jako priorytetem:</p>
          <ul>
            <li>❌ NIE zapisujemy wprowadzonych danych osobowych (imię, nazwisko, data urodzenia)</li>
            <li>❌ NIE zapisujemy obliczonych kodów podatkowych</li>
            <li>❌ NIE tworzymy profili użytkowników</li>
            <li>❌ NIE sprzedajemy danych osobom trzecim</li>
          </ul>
          <p><strong>Wszystkie obliczenia odbywają się w Twojej przeglądarce. Twoje dane nigdy nie są wysyłane na nasze serwery.</strong></p>
          
          <h2>Reklamy (Google AdSense)</h2>
          <p>Używamy Google AdSense do wyświetlania reklam na naszej stronie. Google i jego partnerzy reklamowi używają plików cookie do wyświetlania reklam na podstawie poprzednich wizyt użytkowników na naszej stronie lub innych stronach internetowych.</p>
          <p>Wykorzystanie plików cookie przez Google umożliwia Google i jego partnerom wyświetlanie reklam użytkownikom na podstawie ich wizyty na naszych stronach i/lub innych stronach internetowych.</p>
          <p>Możesz zrezygnować z reklam spersonalizowanych, odwiedzając <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Ustawienia reklam Google</a>.</p>
          <p>Alternatywnie możesz wyłączyć wykorzystanie plików cookie przez dostawców zewnętrznych do reklam spersonalizowanych, odwiedzając: <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">www.aboutads.info/choices</a></p>
          <p>Więcej informacji o tym, jak Google wykorzystuje dane podczas odwiedzania naszej strony, znajdziesz pod adresem: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/partner-sites</a></p>
          
          <h2>Pliki cookie</h2>
          <p>Używamy tylko niezbędnych technicznych plików cookie oraz, za zgodą, analitycznych i reklamowych plików cookie. Zobacz naszą <a href="/pl/cookies">Politykę Cookies</a> po szczegóły.</p>
          
          <h2>Twoje prawa (RODO Art. 15-22)</h2>
          <p>Masz prawo dostępu, sprostowania, usunięcia, przenoszenia, sprzeciwu i wycofania zgody.</p>
          
          <h2>Kontakt</h2>
          <p>W sprawach dotyczących prywatności: privacy@codicefiscale.expert</p>
        </div>
      )
    };
    return privacyTexts[locale] || privacyTexts.en;
  };

  const content = {
    privacy: {
      title: t('nav.privacy'),
      content: getPrivacyContent()
    },
    cookie: {
      title: t('nav.cookie'),
      content: locale === 'it' ? (
        <div className="prose max-w-none">
          <h1>Cookie Policy</h1>
          <p><strong>Ultimo aggiornamento:</strong> Dicembre 2025</p>
          
          <h2>Cookie Strettamente Necessari</h2>
          <p>Essenziali per il funzionamento del sito, non possono essere disattivati.</p>
          <ul>
            <li><code>cf_locale</code> - Preferenza lingua (1 anno)</li>
            <li><code>cookie_consent</code> - Preferenze cookie (1 anno)</li>
          </ul>
          
          <h2>Cookie Analitici (previo consenso)</h2>
          <p>Google Analytics (GA4) per capire come i visitatori interagiscono con il sito. Raccoglie dati anonimi su pagine visitate, tempo di permanenza e origine del traffico.</p>
          <ul>
            <li><code>_ga</code> - ID utente anonimo (2 anni)</li>
            <li><code>_ga_*</code> - Sessione di navigazione (2 anni)</li>
          </ul>
          
          <h2>Cookie Pubblicitari (previo consenso)</h2>
          <p>Utilizziamo Google AdSense per mostrare annunci pubblicitari. Google e i suoi partner utilizzano cookie per personalizzare gli annunci in base alle tue visite precedenti su questo e altri siti web.</p>
          <ul>
            <li><code>__gads</code> - Targeting pubblicitario</li>
            <li><code>__gpi</code> - Gestione annunci</li>
            <li><code>IDE</code>, <code>DSID</code> - Preferenze pubblicitarie (DoubleClick)</li>
          </ul>
          <p>L'utilizzo dei cookie da parte di Google consente a Google e ai suoi partner di pubblicare annunci in base alla tua visita sui nostri siti e/o su altri siti Internet.</p>
          <p><strong>Opt-out pubblicità personalizzata:</strong></p>
          <ul>
            <li><a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Impostazioni annunci Google</a></li>
            <li><a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">www.aboutads.info/choices</a></li>
          </ul>
          <p>Per sapere come Google utilizza i tuoi dati: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">policies.google.com/technologies/partner-sites</a></p>
          
          <h2>Come gestire i cookie</h2>
          <p>Puoi modificare le tue preferenze in qualsiasi momento cliccando sull'icona cookie in basso a sinistra o su "Impostazioni Cookie" nel footer.</p>
        </div>
      ) : (
        <div className="prose max-w-none">
          <h1>Cookie Policy</h1>
          <p><strong>Last updated:</strong> December 2025</p>
          
          <h2>Strictly Necessary Cookies</h2>
          <p>Essential for site functionality, cannot be disabled.</p>
          <ul>
            <li><code>cf_locale</code> - Language preference (1 year)</li>
            <li><code>cookie_consent</code> - Cookie preferences (1 year)</li>
          </ul>
          
          <h2>Analytics Cookies (with consent)</h2>
          <p>Google Analytics (GA4) to understand how visitors interact with the site. Collects anonymous data about pages visited, time spent and traffic source.</p>
          <ul>
            <li><code>_ga</code> - Anonymous user ID (2 years)</li>
            <li><code>_ga_*</code> - Browsing session (2 years)</li>
          </ul>
          
          <h2>Advertising Cookies (with consent)</h2>
          <p>We use Google AdSense to display advertisements. Google and its partners use cookies to personalize ads based on your prior visits to this and other websites.</p>
          <ul>
            <li><code>__gads</code> - Ad targeting</li>
            <li><code>__gpi</code> - Ad management</li>
            <li><code>IDE</code>, <code>DSID</code> - Ad preferences (DoubleClick)</li>
          </ul>
          <p>Google's use of cookies enables Google and its partners to serve ads based on your visit to our sites and/or other sites on the Internet.</p>
          <p><strong>Opt-out of personalized advertising:</strong></p>
          <ul>
            <li><a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Google Ads Settings</a></li>
            <li><a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">www.aboutads.info/choices</a></li>
          </ul>
          <p>To learn how Google uses your data: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">policies.google.com/technologies/partner-sites</a></p>
          
          <h2>How to manage cookies</h2>
          <p>You can change your preferences at any time by clicking the cookie icon at the bottom left or "Cookie Settings" in the footer.</p>
        </div>
      )
    },
    terms: {
      title: t('nav.terms'),
      content: locale === 'it' ? (
        <div className="prose max-w-none">
          <h1>Termini di Servizio</h1>
          <p><strong>Ultimo aggiornamento:</strong> Agosto 2025</p>
          
          <h2>Uso del Servizio</h2>
          <p>CodiceFiscale.expert fornisce strumenti gratuiti per il calcolo del Codice Fiscale italiano.</p>
          
          <h2>Limitazione di Responsabilità</h2>
          <p>Il servizio è fornito "così com'è". Non garantiamo che i risultati siano ufficialmente riconosciuti. Per il rilascio ufficiale del Codice Fiscale, rivolgersi all'Agenzia delle Entrate.</p>
          
          <h2>Tessera Sanitaria</h2>
          <p>I PDF generati sono solo a scopo dimostrativo e NON sono documenti ufficiali.</p>
          
          <h2>Proprietà Intellettuale</h2>
          <p>Tutti i contenuti del sito sono protetti da copyright. È vietata la riproduzione senza autorizzazione.</p>
        </div>
      ) : (
        <div className="prose max-w-none">
          <h1>Terms of Service</h1>
          <p><strong>Last updated:</strong> August 2025</p>
          
          <h2>Use of Service</h2>
          <p>CodiceFiscale.expert provides free tools for calculating the Italian Tax Code.</p>
          
          <h2>Limitation of Liability</h2>
          <p>The service is provided "as is". We do not guarantee that results are officially recognized. For official Tax Code issuance, contact the Revenue Agency.</p>
          
          <h2>Health Card</h2>
          <p>Generated PDFs are for demonstration purposes only and are NOT official documents.</p>
          
          <h2>Intellectual Property</h2>
          <p>All site content is protected by copyright. Reproduction without authorization is prohibited.</p>
        </div>
      )
    },
    accessibility: {
      title: t('nav.accessibility'),
      content: locale === 'it' ? (
        <div className="prose max-w-none">
          <h1>Dichiarazione di Accessibilità</h1>
          <p>CodiceFiscale.expert si impegna a garantire l'accessibilità digitale per le persone con disabilità.</p>
          
          <h2>Standard di conformità</h2>
          <p>Questo sito è progettato per essere conforme alle linee guida WCAG 2.1 livello AA.</p>
          
          <h2>Funzionalità di accessibilità</h2>
          <ul>
            <li>✅ Navigazione completa da tastiera</li>
            <li>✅ Focus visibile su tutti gli elementi interattivi</li>
            <li>✅ Contrasto colori adeguato (minimo 4.5:1)</li>
            <li>✅ Testo ridimensionabile fino al 200%</li>
            <li>✅ Compatibilità screen reader (NVDA, JAWS, VoiceOver)</li>
            <li>✅ Skip link per saltare al contenuto principale</li>
            <li>✅ Widget di accessibilità integrato</li>
          </ul>
          
          <h2>Widget di Accessibilità</h2>
          <p>Utilizza il pulsante blu in basso a sinistra per:</p>
          <ul>
            <li>Aumentare/diminuire la dimensione del testo</li>
            <li>Attivare modalità alto contrasto</li>
            <li>Evidenziare i link</li>
            <li>Attivare cursore grande</li>
            <li>Usare font più leggibile</li>
            <li>Fermare le animazioni</li>
          </ul>
          
          <h2>Feedback</h2>
          <p>Riscontri problemi di accessibilità? Contattaci: accessibility@codicefiscale.expert</p>
        </div>
      ) : (
        <div className="prose max-w-none">
          <h1>Accessibility Statement</h1>
          <p>CodiceFiscale.expert is committed to ensuring digital accessibility for people with disabilities.</p>
          
          <h2>Conformance standard</h2>
          <p>This site is designed to conform to WCAG 2.1 level AA guidelines.</p>
          
          <h2>Accessibility features</h2>
          <ul>
            <li>✅ Full keyboard navigation</li>
            <li>✅ Visible focus on all interactive elements</li>
            <li>✅ Adequate color contrast (minimum 4.5:1)</li>
            <li>✅ Text resizable up to 200%</li>
            <li>✅ Screen reader compatibility (NVDA, JAWS, VoiceOver)</li>
            <li>✅ Skip link to main content</li>
            <li>✅ Integrated accessibility widget</li>
          </ul>
          
          <h2>Accessibility Widget</h2>
          <p>Use the blue button at the bottom left to:</p>
          <ul>
            <li>Increase/decrease text size</li>
            <li>Enable high contrast mode</li>
            <li>Highlight links</li>
            <li>Enable large cursor</li>
            <li>Use more readable font</li>
            <li>Stop animations</li>
          </ul>
          
          <h2>Feedback</h2>
          <p>Experiencing accessibility issues? Contact us: accessibility@codicefiscale.expert</p>
        </div>
      )
    }
  };

  const pageContent = content[type] || content.privacy;

  // Get meta from translations
  const getMetaKey = () => {
    const metaMap = {
      'privacy': 'privacy',
      'cookie': 'cookie',
      'terms': 'terms',
      'accessibility': 'accessibility'
    };
    return metaMap[type] || 'privacy';
  };

  // Self-canonical to the real localized route (e.g. /termini, /de/nutzungsbedingungen),
  // never to the bare English type ("/terms" is not a route and serves the empty shell)
  const localizedRoute = ROUTE_MAPPINGS[locale]?.[type] || ROUTE_MAPPINGS.it[type];
  const canonicalUrl = locale === 'it'
    ? `https://codicefiscale.expert/${localizedRoute}`
    : `https://codicefiscale.expert/${locale}/${localizedRoute}`;

  return (
    <>
      {/* defer={false}: synchronous head flush, react-snap can snapshot before
          the deferred rAF flush and lose title/canonical */}
      <Helmet defer={false}>
        <title>{t(`meta.${getMetaKey()}.title`)}</title>
        <meta name="description" content={t(`meta.${getMetaKey()}.description`)} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <section className="py-12 bg-gray-50 min-h-[70vh]" id="main-content">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            {pageContent.content}
          </div>
        </div>
      </section>
    </>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// App Layout with locale sync
const AppLayout = ({ children }) => {
  const location = useLocation();
  const { locale } = useI18n();
  const [syncDone, setSyncDone] = React.useState(false);
  
  // Sync locale from URL on first load only
  React.useEffect(() => {
    if (syncDone) return;
    // Skip during build-time prerender (react-snap/puppeteer set navigator.webdriver): there is no
    // user to sync, and reload() races across react-snap's shared-localStorage session -> reload loop.
    if (navigator.webdriver) { setSyncDone(true); return; }

    const segments = location.pathname.split('/').filter(Boolean);
    const urlLang = segments[0];
    const supportedLangs = ['it', 'en', 'de', 'fr', 'es', 'pt', 'ro', 'pl'];
    
    let targetLang = 'it';
    if (supportedLangs.includes(urlLang)) {
      targetLang = urlLang;
    }
    
    const savedLang = localStorage.getItem('cf_locale');
    if (savedLang !== targetLang) {
      localStorage.setItem('cf_locale', targetLang);
      setSyncDone(true);
      // Only reload if language actually needs to change
      if (savedLang && savedLang !== targetLang) {
        window.location.reload();
      }
    }
    setSyncDone(true);
  }, []);

  // GA4 page view on SPA route change. No-op until GA4 is loaded (analytics consent
  // granted, not during prerender) — see analytics.trackPageView / CookieContext.loadAnalytics.
  React.useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <ScrollToTop />
      <Header />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
      <CookieBanner />
      <AccessibilityWidget />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <I18nProvider>
        <CookieProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <AppLayout>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <Routes>
                {/* Italian routes (default, no prefix) */}
                <Route path="/" element={<HomePage />} />
                <Route path="/calcolo" element={<HomePage />} />
                <Route path="/inverso" element={<ReversePage />} />
                <Route path="/verifica" element={<VerifyPage />} />
                <Route path="/tessera-sanitaria" element={<HealthCardPage />} />
                <Route path="/comuni" element={<MunicipalitiesPage />} />
                <Route path="/provincia/:sigla" element={<ProvincePage />} />
                <Route path="/regione/:slug" element={<RegionPage />} />
                <Route path="/come-funziona" element={<HowItWorksPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/guida-codice-fiscale" element={<GuidaCodiceFiscale />} />
                <Route path="/omocodia" element={<OmocodiaPage />} />
                <Route path="/privacy" element={<LegalPage type="privacy" />} />
                <Route path="/cookie" element={<LegalPage type="cookie" />} />
                <Route path="/termini" element={<LegalPage type="terms" />} />
                <Route path="/accessibilita" element={<LegalPage type="accessibility" />} />
                <Route path="/chi-siamo" element={<AboutPage />} />
                <Route path="/contatti" element={<ContactPage />} />
                
                {/* Italian Blog routes */}
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/come-calcolare-codice-fiscale" element={<BlogArticles.ArticleComeCalcolareCodiceFiscale />} />
                <Route path="/blog/codice-fiscale-stranieri" element={<BlogArticles.ArticleCodiceFiscaleStranieri />} />
                <Route path="/blog/codice-fiscale-neonati" element={<BlogArticles.ArticleCodiceFiscaleNeonati />} />
                <Route path="/blog/documenti-codice-fiscale" element={<BlogArticles.ArticleDocumentiCodiceFiscale />} />
                <Route path="/blog/errori-calcolo-codice-fiscale" element={<BlogArticles.ArticleErroriCalcolo />} />
                <Route path="/blog/omocodia-spiegata" element={<BlogArticles.ArticleOmocodiaSpiegata />} />
                <Route path="/blog/carattere-controllo-cin" element={<BlogArticles.ArticleCarattereControllo />} />
                <Route path="/blog/codici-catastali-comuni" element={<BlogArticles.ArticleCodiciCatastali />} />
                <Route path="/blog/differenza-codice-fiscale-partita-iva" element={<BlogArticles.ArticleDifferenzaCfPiva />} />
                <Route path="/blog/struttura-codice-fiscale" element={<BlogArticles.ArticleStrutturaCF />} />
                <Route path="/blog/tessera-sanitaria-guida" element={<BlogArticles.ArticleTesseraSanitariaGuida />} />
                <Route path="/blog/tessera-sanitaria-europea-team" element={<BlogArticles.ArticleTesseraEuropeaTEAM />} />
                <Route path="/blog/codice-fiscale-carta-identita" element={<BlogArticles.ArticleCFCartaIdentita />} />
                <Route path="/blog/codice-fiscale-spid" element={<BlogArticles.ArticleCFSpid />} />
                <Route path="/blog/codice-fiscale-cittadini-ue" element={<BlogArticles.ArticleCFCittadiniUE />} />
                <Route path="/blog/codice-fiscale-provvisorio" element={<BlogArticles.ArticleCFProvvisorio />} />
                <Route path="/blog/cambio-dati-codice-fiscale" element={<BlogArticles.ArticleCambioDati />} />
                <Route path="/blog/codice-fiscale-deceduti" element={<BlogArticles.ArticleCFDeceduti />} />
                <Route path="/blog/verifica-codice-fiscale-online" element={<BlogArticles.ArticleVerificaOnline />} />
                <Route path="/blog/storia-codice-fiscale" element={<BlogArticles.ArticleStoriaCF />} />
                
                {/* English routes */}
                <Route path="/en" element={<HomePage />} />
                <Route path="/en/calculator" element={<HomePage />} />
                <Route path="/en/reverse" element={<ReversePage />} />
                <Route path="/en/verify" element={<VerifyPage />} />
                <Route path="/en/health-card" element={<HealthCardPage />} />
                <Route path="/en/municipalities" element={<MunicipalitiesPage />} />
                <Route path="/en/province/:sigla" element={<ProvincePage />} />
                <Route path="/en/region/:slug" element={<RegionPage />} />
                <Route path="/en/how-it-works" element={<HowItWorksPage />} />
                <Route path="/en/faq" element={<FAQPage />} />
                <Route path="/en/guide" element={<GuidaCodiceFiscale />} />
                <Route path="/en/omocodia" element={<OmocodiaPage />} />
                <Route path="/en/privacy" element={<LegalPage type="privacy" />} />
                <Route path="/en/cookie" element={<LegalPage type="cookie" />} />
                <Route path="/en/terms" element={<LegalPage type="terms" />} />
                <Route path="/en/accessibility" element={<LegalPage type="accessibility" />} />
                <Route path="/en/about" element={<AboutPage />} />
                <Route path="/en/contact" element={<ContactPage />} />
                
                {/* English Blog routes */}
                <Route path="/en/blog" element={<BlogIndex />} />
                <Route path="/en/blog/how-to-calculate-tax-code" element={<BlogArticles.ArticleComeCalcolareCodiceFiscale />} />
                <Route path="/en/blog/tax-code-foreigners" element={<BlogArticles.ArticleCodiceFiscaleStranieri />} />
                <Route path="/en/blog/tax-code-newborns" element={<BlogArticles.ArticleCodiceFiscaleNeonati />} />
                <Route path="/en/blog/documents-tax-code" element={<BlogArticles.ArticleDocumentiCodiceFiscale />} />
                <Route path="/en/blog/tax-code-calculation-errors" element={<BlogArticles.ArticleErroriCalcolo />} />
                <Route path="/en/blog/omocodia-explained" element={<BlogArticles.ArticleOmocodiaSpiegata />} />
                <Route path="/en/blog/control-character-cin" element={<BlogArticles.ArticleCarattereControllo />} />
                <Route path="/en/blog/cadastral-codes-municipalities" element={<BlogArticles.ArticleCodiciCatastali />} />
                <Route path="/en/blog/difference-tax-code-vat-number" element={<BlogArticles.ArticleDifferenzaCfPiva />} />
                <Route path="/en/blog/tax-code-structure" element={<BlogArticles.ArticleStrutturaCF />} />
                <Route path="/en/blog/health-card-guide" element={<BlogArticles.ArticleTesseraSanitariaGuida />} />
                <Route path="/en/blog/european-health-card-ehic" element={<BlogArticles.ArticleTesseraEuropeaTEAM />} />
                <Route path="/en/blog/tax-code-id-card" element={<BlogArticles.ArticleCFCartaIdentita />} />
                <Route path="/en/blog/tax-code-digital-identity" element={<BlogArticles.ArticleCFSpid />} />
                <Route path="/en/blog/tax-code-eu-citizens" element={<BlogArticles.ArticleCFCittadiniUE />} />
                <Route path="/en/blog/temporary-tax-code" element={<BlogArticles.ArticleCFProvvisorio />} />
                <Route path="/en/blog/change-tax-code-data" element={<BlogArticles.ArticleCambioDati />} />
                <Route path="/en/blog/tax-code-deceased" element={<BlogArticles.ArticleCFDeceduti />} />
                <Route path="/en/blog/verify-tax-code-online" element={<BlogArticles.ArticleVerificaOnline />} />
                <Route path="/en/blog/history-italian-tax-code" element={<BlogArticles.ArticleStoriaCF />} />
                
                {/* German routes */}
                <Route path="/de" element={<HomePage />} />
                <Route path="/de/rechner" element={<HomePage />} />
                <Route path="/de/umkehren" element={<ReversePage />} />
                <Route path="/de/pruefen" element={<VerifyPage />} />
                <Route path="/de/gesundheitskarte" element={<HealthCardPage />} />
                <Route path="/de/gemeinden" element={<MunicipalitiesPage />} />
                <Route path="/de/provinz/:sigla" element={<ProvincePage />} />
                <Route path="/de/region/:slug" element={<RegionPage />} />
                <Route path="/de/wie-funktioniert" element={<HowItWorksPage />} />
                <Route path="/de/faq" element={<FAQPage />} />
                <Route path="/de/anleitung" element={<GuidaCodiceFiscale />} />
                <Route path="/de/omocodia" element={<OmocodiaPage />} />
                <Route path="/de/datenschutz" element={<LegalPage type="privacy" />} />
                <Route path="/de/cookie" element={<LegalPage type="cookie" />} />
                <Route path="/de/nutzungsbedingungen" element={<LegalPage type="terms" />} />
                <Route path="/de/barrierefreiheit" element={<LegalPage type="accessibility" />} />
                <Route path="/de/about" element={<AboutPage />} />
                <Route path="/de/contact" element={<ContactPage />} />
                
                {/* German Blog routes */}
                <Route path="/de/blog" element={<BlogIndex />} />
                <Route path="/de/blog/steuernummer-berechnen" element={<BlogArticles.ArticleComeCalcolareCodiceFiscale />} />
                <Route path="/de/blog/steuernummer-auslaender" element={<BlogArticles.ArticleCodiceFiscaleStranieri />} />
                <Route path="/de/blog/steuernummer-neugeborene" element={<BlogArticles.ArticleCodiceFiscaleNeonati />} />
                <Route path="/de/blog/dokumente-steuernummer" element={<BlogArticles.ArticleDocumentiCodiceFiscale />} />
                <Route path="/de/blog/fehler-steuernummer-berechnung" element={<BlogArticles.ArticleErroriCalcolo />} />
                <Route path="/de/blog/omocodia-erklaert" element={<BlogArticles.ArticleOmocodiaSpiegata />} />
                <Route path="/de/blog/kontrollzeichen-cin" element={<BlogArticles.ArticleCarattereControllo />} />
                <Route path="/de/blog/katastercodes-gemeinden" element={<BlogArticles.ArticleCodiciCatastali />} />
                <Route path="/de/blog/unterschied-steuernummer-umsatzsteuer" element={<BlogArticles.ArticleDifferenzaCfPiva />} />
                <Route path="/de/blog/struktur-steuernummer" element={<BlogArticles.ArticleStrutturaCF />} />
                <Route path="/de/blog/gesundheitskarte-anleitung" element={<BlogArticles.ArticleTesseraSanitariaGuida />} />
                <Route path="/de/blog/europaeische-krankenversicherungskarte" element={<BlogArticles.ArticleTesseraEuropeaTEAM />} />
                <Route path="/de/blog/steuernummer-personalausweis" element={<BlogArticles.ArticleCFCartaIdentita />} />
                <Route path="/de/blog/steuernummer-digitale-identitaet" element={<BlogArticles.ArticleCFSpid />} />
                <Route path="/de/blog/steuernummer-eu-buerger" element={<BlogArticles.ArticleCFCittadiniUE />} />
                <Route path="/de/blog/vorlaeufige-steuernummer" element={<BlogArticles.ArticleCFProvvisorio />} />
                <Route path="/de/blog/aenderung-steuernummer-daten" element={<BlogArticles.ArticleCambioDati />} />
                <Route path="/de/blog/steuernummer-verstorbene" element={<BlogArticles.ArticleCFDeceduti />} />
                <Route path="/de/blog/steuernummer-online-pruefen" element={<BlogArticles.ArticleVerificaOnline />} />
                <Route path="/de/blog/geschichte-steuernummer" element={<BlogArticles.ArticleStoriaCF />} />
                
                {/* French routes */}
                <Route path="/fr" element={<HomePage />} />
                <Route path="/fr/calculateur" element={<HomePage />} />
                <Route path="/fr/inverse" element={<ReversePage />} />
                <Route path="/fr/verifier" element={<VerifyPage />} />
                <Route path="/fr/carte-sante" element={<HealthCardPage />} />
                <Route path="/fr/communes" element={<MunicipalitiesPage />} />
                <Route path="/fr/province/:sigla" element={<ProvincePage />} />
                <Route path="/fr/region/:slug" element={<RegionPage />} />
                <Route path="/fr/comment-ca-marche" element={<HowItWorksPage />} />
                <Route path="/fr/faq" element={<FAQPage />} />
                <Route path="/fr/guide" element={<GuidaCodiceFiscale />} />
                <Route path="/fr/omocodia" element={<OmocodiaPage />} />
                <Route path="/fr/confidentialite" element={<LegalPage type="privacy" />} />
                <Route path="/fr/cookies" element={<LegalPage type="cookie" />} />
                <Route path="/fr/conditions" element={<LegalPage type="terms" />} />
                <Route path="/fr/accessibilite" element={<LegalPage type="accessibility" />} />
                <Route path="/fr/about" element={<AboutPage />} />
                <Route path="/fr/contact" element={<ContactPage />} />
                
                {/* French Blog routes */}
                <Route path="/fr/blog" element={<BlogIndex />} />
                <Route path="/fr/blog/comment-calculer-code-fiscal" element={<BlogArticles.ArticleComeCalcolareCodiceFiscale />} />
                <Route path="/fr/blog/code-fiscal-etrangers" element={<BlogArticles.ArticleCodiceFiscaleStranieri />} />
                <Route path="/fr/blog/code-fiscal-nouveau-nes" element={<BlogArticles.ArticleCodiceFiscaleNeonati />} />
                <Route path="/fr/blog/documents-code-fiscal" element={<BlogArticles.ArticleDocumentiCodiceFiscale />} />
                <Route path="/fr/blog/erreurs-calcul-code-fiscal" element={<BlogArticles.ArticleErroriCalcolo />} />
                <Route path="/fr/blog/omocodia-expliquee" element={<BlogArticles.ArticleOmocodiaSpiegata />} />
                <Route path="/fr/blog/caractere-controle-cin" element={<BlogArticles.ArticleCarattereControllo />} />
                <Route path="/fr/blog/codes-cadastraux-communes" element={<BlogArticles.ArticleCodiciCatastali />} />
                <Route path="/fr/blog/difference-code-fiscal-tva" element={<BlogArticles.ArticleDifferenzaCfPiva />} />
                <Route path="/fr/blog/structure-code-fiscal" element={<BlogArticles.ArticleStrutturaCF />} />
                <Route path="/fr/blog/carte-sante-guide" element={<BlogArticles.ArticleTesseraSanitariaGuida />} />
                <Route path="/fr/blog/carte-europeenne-assurance-maladie" element={<BlogArticles.ArticleTesseraEuropeaTEAM />} />
                <Route path="/fr/blog/code-fiscal-carte-identite" element={<BlogArticles.ArticleCFCartaIdentita />} />
                <Route path="/fr/blog/code-fiscal-identite-numerique" element={<BlogArticles.ArticleCFSpid />} />
                <Route path="/fr/blog/code-fiscal-citoyens-ue" element={<BlogArticles.ArticleCFCittadiniUE />} />
                <Route path="/fr/blog/code-fiscal-provisoire" element={<BlogArticles.ArticleCFProvvisorio />} />
                <Route path="/fr/blog/modification-donnees-code-fiscal" element={<BlogArticles.ArticleCambioDati />} />
                <Route path="/fr/blog/code-fiscal-defunts" element={<BlogArticles.ArticleCFDeceduti />} />
                <Route path="/fr/blog/verifier-code-fiscal-en-ligne" element={<BlogArticles.ArticleVerificaOnline />} />
                <Route path="/fr/blog/histoire-code-fiscal" element={<BlogArticles.ArticleStoriaCF />} />
                
                {/* Spanish routes */}
                <Route path="/es" element={<HomePage />} />
                <Route path="/es/calculadora" element={<HomePage />} />
                <Route path="/es/inverso" element={<ReversePage />} />
                <Route path="/es/verificar" element={<VerifyPage />} />
                <Route path="/es/tarjeta-sanitaria" element={<HealthCardPage />} />
                <Route path="/es/municipios" element={<MunicipalitiesPage />} />
                <Route path="/es/provincia/:sigla" element={<ProvincePage />} />
                <Route path="/es/region/:slug" element={<RegionPage />} />
                <Route path="/es/como-funciona" element={<HowItWorksPage />} />
                <Route path="/es/faq" element={<FAQPage />} />
                <Route path="/es/guia" element={<GuidaCodiceFiscale />} />
                <Route path="/es/omocodia" element={<OmocodiaPage />} />
                <Route path="/es/privacidad" element={<LegalPage type="privacy" />} />
                <Route path="/es/cookies" element={<LegalPage type="cookie" />} />
                <Route path="/es/terminos" element={<LegalPage type="terms" />} />
                <Route path="/es/accesibilidad" element={<LegalPage type="accessibility" />} />
                <Route path="/es/about" element={<AboutPage />} />
                <Route path="/es/contact" element={<ContactPage />} />
                
                {/* Spanish Blog routes */}
                <Route path="/es/blog" element={<BlogIndex />} />
                <Route path="/es/blog/como-calcular-codigo-fiscal" element={<BlogArticles.ArticleComeCalcolareCodiceFiscale />} />
                <Route path="/es/blog/codigo-fiscal-extranjeros" element={<BlogArticles.ArticleCodiceFiscaleStranieri />} />
                <Route path="/es/blog/codigo-fiscal-recien-nacidos" element={<BlogArticles.ArticleCodiceFiscaleNeonati />} />
                <Route path="/es/blog/documentos-codigo-fiscal" element={<BlogArticles.ArticleDocumentiCodiceFiscale />} />
                <Route path="/es/blog/errores-calculo-codigo-fiscal" element={<BlogArticles.ArticleErroriCalcolo />} />
                <Route path="/es/blog/omocodia-explicada" element={<BlogArticles.ArticleOmocodiaSpiegata />} />
                <Route path="/es/blog/caracter-control-cin" element={<BlogArticles.ArticleCarattereControllo />} />
                <Route path="/es/blog/codigos-catastrales-municipios" element={<BlogArticles.ArticleCodiciCatastali />} />
                <Route path="/es/blog/diferencia-codigo-fiscal-iva" element={<BlogArticles.ArticleDifferenzaCfPiva />} />
                <Route path="/es/blog/estructura-codigo-fiscal" element={<BlogArticles.ArticleStrutturaCF />} />
                <Route path="/es/blog/tarjeta-sanitaria-guia" element={<BlogArticles.ArticleTesseraSanitariaGuida />} />
                <Route path="/es/blog/tarjeta-sanitaria-europea" element={<BlogArticles.ArticleTesseraEuropeaTEAM />} />
                <Route path="/es/blog/codigo-fiscal-dni" element={<BlogArticles.ArticleCFCartaIdentita />} />
                <Route path="/es/blog/codigo-fiscal-identidad-digital" element={<BlogArticles.ArticleCFSpid />} />
                <Route path="/es/blog/codigo-fiscal-ciudadanos-ue" element={<BlogArticles.ArticleCFCittadiniUE />} />
                <Route path="/es/blog/codigo-fiscal-provisional" element={<BlogArticles.ArticleCFProvvisorio />} />
                <Route path="/es/blog/cambio-datos-codigo-fiscal" element={<BlogArticles.ArticleCambioDati />} />
                <Route path="/es/blog/codigo-fiscal-fallecidos" element={<BlogArticles.ArticleCFDeceduti />} />
                <Route path="/es/blog/verificar-codigo-fiscal-online" element={<BlogArticles.ArticleVerificaOnline />} />
                <Route path="/es/blog/historia-codigo-fiscal" element={<BlogArticles.ArticleStoriaCF />} />
                
                {/* Portuguese routes */}
                <Route path="/pt" element={<HomePage />} />
                <Route path="/pt/calculadora" element={<HomePage />} />
                <Route path="/pt/inverso" element={<ReversePage />} />
                <Route path="/pt/verificar" element={<VerifyPage />} />
                <Route path="/pt/cartao-saude" element={<HealthCardPage />} />
                <Route path="/pt/municipios" element={<MunicipalitiesPage />} />
                <Route path="/pt/provincia/:sigla" element={<ProvincePage />} />
                <Route path="/pt/regiao/:slug" element={<RegionPage />} />
                <Route path="/pt/como-funciona" element={<HowItWorksPage />} />
                <Route path="/pt/faq" element={<FAQPage />} />
                <Route path="/pt/guia" element={<GuidaCodiceFiscale />} />
                <Route path="/pt/omocodia" element={<OmocodiaPage />} />
                <Route path="/pt/privacidade" element={<LegalPage type="privacy" />} />
                <Route path="/pt/cookies" element={<LegalPage type="cookie" />} />
                <Route path="/pt/termos" element={<LegalPage type="terms" />} />
                <Route path="/pt/acessibilidade" element={<LegalPage type="accessibility" />} />
                <Route path="/pt/about" element={<AboutPage />} />
                <Route path="/pt/contact" element={<ContactPage />} />
                
                {/* Portuguese Blog routes */}
                <Route path="/pt/blog" element={<BlogIndex />} />
                <Route path="/pt/blog/como-calcular-codigo-fiscal" element={<BlogArticles.ArticleComeCalcolareCodiceFiscale />} />
                <Route path="/pt/blog/codigo-fiscal-estrangeiros" element={<BlogArticles.ArticleCodiceFiscaleStranieri />} />
                <Route path="/pt/blog/codigo-fiscal-recem-nascidos" element={<BlogArticles.ArticleCodiceFiscaleNeonati />} />
                <Route path="/pt/blog/documentos-codigo-fiscal" element={<BlogArticles.ArticleDocumentiCodiceFiscale />} />
                <Route path="/pt/blog/erros-calculo-codigo-fiscal" element={<BlogArticles.ArticleErroriCalcolo />} />
                <Route path="/pt/blog/omocodia-explicada" element={<BlogArticles.ArticleOmocodiaSpiegata />} />
                <Route path="/pt/blog/caracter-controle-cin" element={<BlogArticles.ArticleCarattereControllo />} />
                <Route path="/pt/blog/codigos-cadastrais-municipios" element={<BlogArticles.ArticleCodiciCatastali />} />
                <Route path="/pt/blog/diferenca-codigo-fiscal-nif" element={<BlogArticles.ArticleDifferenzaCfPiva />} />
                <Route path="/pt/blog/estrutura-codigo-fiscal" element={<BlogArticles.ArticleStrutturaCF />} />
                <Route path="/pt/blog/cartao-saude-guia" element={<BlogArticles.ArticleTesseraSanitariaGuida />} />
                <Route path="/pt/blog/cartao-europeu-saude" element={<BlogArticles.ArticleTesseraEuropeaTEAM />} />
                <Route path="/pt/blog/codigo-fiscal-cartao-cidadao" element={<BlogArticles.ArticleCFCartaIdentita />} />
                <Route path="/pt/blog/codigo-fiscal-identidade-digital" element={<BlogArticles.ArticleCFSpid />} />
                <Route path="/pt/blog/codigo-fiscal-cidadaos-ue" element={<BlogArticles.ArticleCFCittadiniUE />} />
                <Route path="/pt/blog/codigo-fiscal-provisorio" element={<BlogArticles.ArticleCFProvvisorio />} />
                <Route path="/pt/blog/alteracao-dados-codigo-fiscal" element={<BlogArticles.ArticleCambioDati />} />
                <Route path="/pt/blog/codigo-fiscal-falecidos" element={<BlogArticles.ArticleCFDeceduti />} />
                <Route path="/pt/blog/verificar-codigo-fiscal-online" element={<BlogArticles.ArticleVerificaOnline />} />
                <Route path="/pt/blog/historia-codigo-fiscal" element={<BlogArticles.ArticleStoriaCF />} />
                
                {/* Romanian routes */}
                <Route path="/ro" element={<HomePage />} />
                <Route path="/ro/calculator" element={<HomePage />} />
                <Route path="/ro/invers" element={<ReversePage />} />
                <Route path="/ro/verifica" element={<VerifyPage />} />
                <Route path="/ro/card-sanatate" element={<HealthCardPage />} />
                <Route path="/ro/comune" element={<MunicipalitiesPage />} />
                <Route path="/ro/provincia/:sigla" element={<ProvincePage />} />
                <Route path="/ro/regiune/:slug" element={<RegionPage />} />
                <Route path="/ro/cum-functioneaza" element={<HowItWorksPage />} />
                <Route path="/ro/faq" element={<FAQPage />} />
                <Route path="/ro/ghid" element={<GuidaCodiceFiscale />} />
                <Route path="/ro/omocodia" element={<OmocodiaPage />} />
                <Route path="/ro/confidentialitate" element={<LegalPage type="privacy" />} />
                <Route path="/ro/cookies" element={<LegalPage type="cookie" />} />
                <Route path="/ro/termeni" element={<LegalPage type="terms" />} />
                <Route path="/ro/accesibilitate" element={<LegalPage type="accessibility" />} />
                <Route path="/ro/about" element={<AboutPage />} />
                <Route path="/ro/contact" element={<ContactPage />} />
                
                {/* Romanian Blog routes */}
                <Route path="/ro/blog" element={<BlogIndex />} />
                <Route path="/ro/blog/cum-calculezi-codul-fiscal" element={<BlogArticles.ArticleComeCalcolareCodiceFiscale />} />
                <Route path="/ro/blog/cod-fiscal-straini" element={<BlogArticles.ArticleCodiceFiscaleStranieri />} />
                <Route path="/ro/blog/cod-fiscal-nou-nascuti" element={<BlogArticles.ArticleCodiceFiscaleNeonati />} />
                <Route path="/ro/blog/documente-cod-fiscal" element={<BlogArticles.ArticleDocumentiCodiceFiscale />} />
                <Route path="/ro/blog/erori-calcul-cod-fiscal" element={<BlogArticles.ArticleErroriCalcolo />} />
                <Route path="/ro/blog/omocodia-explicata" element={<BlogArticles.ArticleOmocodiaSpiegata />} />
                <Route path="/ro/blog/caracter-control-cin" element={<BlogArticles.ArticleCarattereControllo />} />
                <Route path="/ro/blog/coduri-cadastrale-comune" element={<BlogArticles.ArticleCodiciCatastali />} />
                <Route path="/ro/blog/diferenta-cod-fiscal-tva" element={<BlogArticles.ArticleDifferenzaCfPiva />} />
                <Route path="/ro/blog/structura-cod-fiscal" element={<BlogArticles.ArticleStrutturaCF />} />
                <Route path="/ro/blog/card-sanatate-ghid" element={<BlogArticles.ArticleTesseraSanitariaGuida />} />
                <Route path="/ro/blog/card-european-sanatate" element={<BlogArticles.ArticleTesseraEuropeaTEAM />} />
                <Route path="/ro/blog/cod-fiscal-carte-identitate" element={<BlogArticles.ArticleCFCartaIdentita />} />
                <Route path="/ro/blog/cod-fiscal-identitate-digitala" element={<BlogArticles.ArticleCFSpid />} />
                <Route path="/ro/blog/cod-fiscal-cetateni-ue" element={<BlogArticles.ArticleCFCittadiniUE />} />
                <Route path="/ro/blog/cod-fiscal-provizoriu" element={<BlogArticles.ArticleCFProvvisorio />} />
                <Route path="/ro/blog/modificare-date-cod-fiscal" element={<BlogArticles.ArticleCambioDati />} />
                <Route path="/ro/blog/cod-fiscal-decedati" element={<BlogArticles.ArticleCFDeceduti />} />
                <Route path="/ro/blog/verifica-cod-fiscal-online" element={<BlogArticles.ArticleVerificaOnline />} />
                <Route path="/ro/blog/istoria-codului-fiscal" element={<BlogArticles.ArticleStoriaCF />} />
                
                {/* Polish routes */}
                <Route path="/pl" element={<HomePage />} />
                <Route path="/pl/kalkulator" element={<HomePage />} />
                <Route path="/pl/odwrotny" element={<ReversePage />} />
                <Route path="/pl/weryfikuj" element={<VerifyPage />} />
                <Route path="/pl/karta-zdrowia" element={<HealthCardPage />} />
                <Route path="/pl/gminy" element={<MunicipalitiesPage />} />
                <Route path="/pl/prowincja/:sigla" element={<ProvincePage />} />
                <Route path="/pl/region/:slug" element={<RegionPage />} />
                <Route path="/pl/jak-dziala" element={<HowItWorksPage />} />
                <Route path="/pl/faq" element={<FAQPage />} />
                <Route path="/pl/przewodnik" element={<GuidaCodiceFiscale />} />
                <Route path="/pl/omokodia" element={<OmocodiaPage />} />
                <Route path="/pl/prywatnosc" element={<LegalPage type="privacy" />} />
                <Route path="/pl/cookies" element={<LegalPage type="cookie" />} />
                <Route path="/pl/regulamin" element={<LegalPage type="terms" />} />
                <Route path="/pl/dostepnosc" element={<LegalPage type="accessibility" />} />
                <Route path="/pl/about" element={<AboutPage />} />
                <Route path="/pl/contact" element={<ContactPage />} />
                
                {/* Polish Blog routes */}
                <Route path="/pl/blog" element={<BlogIndex />} />
                <Route path="/pl/blog/jak-obliczyc-numer-podatkowy" element={<BlogArticles.ArticleComeCalcolareCodiceFiscale />} />
                <Route path="/pl/blog/numer-podatkowy-obcokrajowcy" element={<BlogArticles.ArticleCodiceFiscaleStranieri />} />
                <Route path="/pl/blog/numer-podatkowy-noworodki" element={<BlogArticles.ArticleCodiceFiscaleNeonati />} />
                <Route path="/pl/blog/dokumenty-numer-podatkowy" element={<BlogArticles.ArticleDocumentiCodiceFiscale />} />
                <Route path="/pl/blog/bledy-obliczania-numeru" element={<BlogArticles.ArticleErroriCalcolo />} />
                <Route path="/pl/blog/omokodia-wyjasnienie" element={<BlogArticles.ArticleOmocodiaSpiegata />} />
                <Route path="/pl/blog/znak-kontrolny-cin" element={<BlogArticles.ArticleCarattereControllo />} />
                <Route path="/pl/blog/kody-katastralne-gminy" element={<BlogArticles.ArticleCodiciCatastali />} />
                <Route path="/pl/blog/roznica-numer-podatkowy-vat" element={<BlogArticles.ArticleDifferenzaCfPiva />} />
                <Route path="/pl/blog/struktura-numeru-podatkowego" element={<BlogArticles.ArticleStrutturaCF />} />
                <Route path="/pl/blog/karta-zdrowia-przewodnik" element={<BlogArticles.ArticleTesseraSanitariaGuida />} />
                <Route path="/pl/blog/europejska-karta-zdrowia" element={<BlogArticles.ArticleTesseraEuropeaTEAM />} />
                <Route path="/pl/blog/numer-podatkowy-dowod" element={<BlogArticles.ArticleCFCartaIdentita />} />
                <Route path="/pl/blog/numer-podatkowy-tozsamosc-cyfrowa" element={<BlogArticles.ArticleCFSpid />} />
                <Route path="/pl/blog/numer-podatkowy-obywatele-ue" element={<BlogArticles.ArticleCFCittadiniUE />} />
                <Route path="/pl/blog/tymczasowy-numer-podatkowy" element={<BlogArticles.ArticleCFProvvisorio />} />
                <Route path="/pl/blog/zmiana-danych-numer-podatkowy" element={<BlogArticles.ArticleCambioDati />} />
                <Route path="/pl/blog/numer-podatkowy-zmarlych" element={<BlogArticles.ArticleCFDeceduti />} />
                <Route path="/pl/blog/weryfikacja-numeru-online" element={<BlogArticles.ArticleVerificaOnline />} />
                <Route path="/pl/blog/historia-numeru-podatkowego" element={<BlogArticles.ArticleStoriaCF />} />
                
                {/* 404 - Page Not Found */}
                <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
              </AppLayout>
            </ErrorBoundary>
          </BrowserRouter>
        </CookieProvider>
      </I18nProvider>
    </HelmetProvider>
  );
}

export default App;
